import { useState, useEffect, useRef } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import ProviderPageTabs from "../components/ProviderPageTabs";
import {
  formatCurrency,
  getResearchByNPI,
  getResearchDetailByNPI,
  getDirectResearchByNPI,
  getResearchOwnershipDualInterest,
  getResearchStudiesByNPI,
  getResearchProductsByNPI,
  getOwnershipDetailByNPI,
  getResearchGeneralDualInterestByNPI,
  getDualInterestByNPI,
  getGeneralPaymentsByNPIAndCompany,
  checkExclusions,
  getResearchStatePayments,
} from "../utils/cmsApi";
import { OwnershipVsResearchChart, GeneralVsResearchChart } from "../components/DualInterestCharts";
import { useSubscription } from "../context/SubscriptionContext";
import { useUser } from "@clerk/react";
import WatchProviderButton from "../components/WatchProviderButton";
import { watchCompany, unwatchCompany, getWatchedCompanies, queueConfirmation } from "../utils/alertsApi";
import { apiFetch } from "../utils/apiClient";
import SingleProviderScreenPanel from "../components/SingleProviderScreenPanel";
import CompareButton from "../components/CompareButton";

const ALL_YEARS = [2016, 2017, 2018, 2019, 2020, 2021, 2022, 2023, 2024, 2025];

// ── Company filter dropdown ───────────────────────────────────────────────────
function ResearchCompanyDropdown({ companies, selectedCompanies, onToggle }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    if (open) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const isAll = selectedCompanies.has("ALL");

  function label() {
    if (isAll) return "All companies";
    if (selectedCompanies.size === 1) {
      const name = [...selectedCompanies][0];
      return name.length > 30 ? name.slice(0, 30) + "..." : name;
    }
    return `${selectedCompanies.size} companies selected`;
  }

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 bg-navy-700 border border-white/10 text-white/70 rounded-lg px-3 py-1.5 text-xs hover:border-white/20 transition-colors"
      >
        <span>{label()}</span>
        <span className="text-white/30">{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 bg-navy-800 border border-white/10 rounded-xl overflow-hidden z-50 shadow-xl min-w-56 max-h-64 overflow-y-auto">
          <button
            onClick={() => onToggle("ALL")}
            className={`w-full text-left px-4 py-2.5 text-xs flex items-center gap-2 transition-colors border-b border-white/5 ${
              isAll ? "text-teal-400 bg-teal-500/10" : "text-white/60 hover:text-white hover:bg-navy-700"
            }`}
          >
            <span className={`w-3 h-3 rounded border flex items-center justify-center shrink-0 ${isAll ? "bg-teal-500 border-teal-500" : "border-white/20"}`}>
              {isAll && <span className="text-navy-900 text-xs leading-none">✓</span>}
            </span>
            All companies
          </button>
          {companies.map((name) => {
            const selected = selectedCompanies.has(name);
            return (
              <button
                key={name}
                onClick={() => onToggle(name)}
                className={`w-full text-left px-4 py-2.5 text-xs flex items-center gap-2 transition-colors border-b border-white/5 last:border-0 ${
                  selected ? "text-teal-400 bg-teal-500/5" : "text-white/60 hover:text-white hover:bg-navy-700"
                }`}
              >
                <span className={`w-3 h-3 rounded border flex items-center justify-center shrink-0 ${selected ? "bg-teal-500 border-teal-500" : "border-white/20"}`}>
                  {selected && <span className="text-navy-900 text-xs leading-none">✓</span>}
                </span>
                <span className="truncate">{name.length > 35 ? name.slice(0, 35) + "..." : name}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Research Payment History Chart ───────────────────────────────────────────
function ResearchPaymentChart({ detail, name, npi, directResearch, totalCompanyCount }) {
  const [chartView, setChartView] = useState("absolute");
  const [hoveredYear, setHoveredYear] = useState(null);
  const [selectedCompanies, setSelectedCompanies] = useState(new Set(["ALL"]));

  const COLORS = [
    "rgba(20,184,166,0.8)",
    "rgba(139,92,246,0.8)",
    "rgba(251,191,36,0.8)",
    "rgba(59,130,246,0.8)",
    "rgba(239,68,68,0.8)",
    "rgba(249,115,22,0.8)",
    "rgba(34,197,94,0.8)",
    "rgba(236,72,153,0.8)",
  ];

  // Merge PI-based and direct research company names for dropdown
  const allCompanyNames = [
    ...new Set([
      ...detail.map((d) => d.companyName),
      ...(directResearch || []).map((d) => d.companyName),
    ])
  ].filter(Boolean);

  const companies = allCompanyNames.sort((a, b) => {
    const aVal =
      detail.filter((d) => d.companyName === a).reduce((s, r) => s + r.totalAmount, 0) +
      (directResearch || []).filter((d) => d.companyName === a).reduce((s, r) => s + r.totalAmount, 0);
    const bVal =
      detail.filter((d) => d.companyName === b).reduce((s, r) => s + r.totalAmount, 0) +
      (directResearch || []).filter((d) => d.companyName === b).reduce((s, r) => s + r.totalAmount, 0);
    return bVal - aVal;
  });

  // Top 8 by combined funding for chart display — all companies available in dropdown and export
  const top10Companies = companies.slice(0, 8);

  const companyColorMap = new Map(companies.map((c, i) => [c, COLORS[i % COLORS.length]]));

  const isAll = selectedCompanies.has("ALL");
  const visibleCompanies = isAll
    ? top10Companies
    : companies.filter((c) => selectedCompanies.has(c));

  function toggleCompany(name) {
    if (name === "ALL") { setSelectedCompanies(new Set(["ALL"])); return; }
    const next = new Set(selectedCompanies);
    next.delete("ALL");
    if (next.has(name)) {
      next.delete(name);
      if (next.size === 0) next.add("ALL");
    } else {
      next.add(name);
    }
    setSelectedCompanies(next);
  }

  const yearStacks = ALL_YEARS.map((y) => {
    const segments = visibleCompanies.map((company) => {
      const row = detail.find((d) => d.programYear === y && d.companyName === company);
      return { company, value: row ? row.totalAmount : 0 };
    });
    const total = segments.reduce((s, seg) => s + seg.value, 0);
    return { year: y, segments, total };
  });

  const maxTotal = Math.max(...yearStacks.map((y) => y.total), 1);

  // Direct payments by year for dual-axis overlay
  const directByYear = ALL_YEARS.map((y) => {
    const total = (directResearch || [])
      .filter((d) => d.year === y)
      .reduce((s, d) => s + d.totalAmount, 0);
    return { year: y, total };
  });
  const maxDirect = Math.max(...directByYear.map((d) => d.total), 1);
  const hasDirectData = directByYear.some((d) => d.total > 0);

  // Y-axis label formatter
  function fmtAxis(val) {
    if (val >= 1_000_000) return `$${(val / 1_000_000).toFixed(1)}M`;
    if (val >= 1_000) return `$${(val / 1_000).toFixed(0)}K`;
    return `$${val.toFixed(0)}`;
  }

  // Total for currently visible companies (respects filter selection)
  const filteredTotal = detail
    .filter((d) => isAll ? top10Companies.includes(d.companyName) : selectedCompanies.has(d.companyName))
    .reduce((s, d) => s + d.totalAmount, 0);

  return (
    <div className="card mb-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-white/60 text-xs uppercase tracking-widest">
          Research payment history
        </h2>
        <div className="flex items-center gap-2">
          <ResearchCompanyDropdown
            companies={companies}
            selectedCompanies={selectedCompanies}
            onToggle={toggleCompany}
          />
          <div className="flex items-center bg-navy-700 border border-white/10 rounded-lg overflow-hidden">
            <button
              onClick={() => setChartView("absolute")}
              className={`text-xs px-2.5 py-1.5 transition-all ${
                chartView === "absolute" ? "bg-teal-500/20 text-teal-400" : "text-white/40 hover:text-white/60"
              }`}
            >
              $
            </button>
            <button
              onClick={() => setChartView("normalized")}
              className={`text-xs px-2.5 py-1.5 transition-all ${
                chartView === "normalized" ? "bg-teal-500/20 text-teal-400" : "text-white/40 hover:text-white/60"
              }`}
            >
              %
            </button>
          </div>
        </div>
      </div>

      <div className="relative" style={{ height: "200px" }}>

        {/* Left Y-axis — associated research funding (teal) */}
        <div className="absolute left-0 top-0 bottom-6 flex flex-col justify-between pointer-events-none" style={{ width: "44px" }}>
          <span className="text-teal-400/50" style={{ fontSize: "9px" }}>{fmtAxis(maxTotal)}</span>
          <span className="text-teal-400/50" style={{ fontSize: "9px" }}>{fmtAxis(maxTotal / 2)}</span>
          <span className="text-teal-400/50" style={{ fontSize: "9px" }}>$0</span>
        </div>

        {/* Right Y-axis — direct research payments (amber) */}
        {hasDirectData && (
          <div className="absolute right-0 top-0 bottom-6 flex flex-col justify-between pointer-events-none items-end" style={{ width: "44px" }}>
            <span className="text-amber-400/50" style={{ fontSize: "9px" }}>{fmtAxis(maxDirect)}</span>
            <span className="text-amber-400/50" style={{ fontSize: "9px" }}>{fmtAxis(maxDirect / 2)}</span>
            <span className="text-amber-400/50" style={{ fontSize: "9px" }}>$0</span>
          </div>
        )}

        <div
          className="absolute inset-0 flex items-end gap-1.5 pb-6 pt-8"
          style={{ paddingLeft: "44px", paddingRight: hasDirectData ? "44px" : "0px" }}
        >
          {yearStacks.map(({ year, segments, total }) => {
            const hasData = total > 0;
            const heightPct = chartView === "absolute" ? (total / maxTotal) * 100 : 100;
            const activeSegments = segments.filter((s) => s.value > 0);
            const normalized = segments.map((s) => ({
              ...s,
              pct: total > 0 ? (s.value / total) * 100 : 0,
            }));
            const isHovered = hoveredYear === year;

            return (
              <div
                key={year}
                className="flex-1 flex flex-col items-center justify-end h-full group relative"
                onMouseEnter={() => setHoveredYear(year)}
                onMouseLeave={() => setHoveredYear(null)}
                style={{ cursor: hasData ? "pointer" : "default" }}
              >
                {/* Tooltip */}
                {(hasData || directByYear.find((d) => d.year === year)?.total > 0) && (
                  <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 bg-navy-900 border border-white/10 rounded-lg px-3 py-2 text-xs opacity-0 group-hover:opacity-100 transition-opacity z-20 pointer-events-none"
                    style={{ minWidth: "160px" }}>
                    <div className="flex items-center justify-between gap-4 mb-1.5 pb-1.5 border-b border-white/10">
                      <p className="text-white/50 font-medium">{year}</p>
                      <p className="text-teal-400 font-medium">{formatCurrency(total)}</p>
                    </div>
                    {[...activeSegments].sort((a, b) => b.value - a.value).map(({ company, value }) => (
                      <div key={company} className="flex items-center justify-between gap-3 mt-1">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <div className="w-2 h-2 rounded-sm shrink-0"
                            style={{ backgroundColor: companyColorMap.get(company) }} />
                          <span className="text-white/40 truncate" style={{ maxWidth: "90px" }}>
                            {company.length > 18 ? company.slice(0, 18) + "…" : company}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <span className="text-white/60">{formatCurrency(value)}</span>
                          <span className="text-white/30">{total > 0 ? `${((value / total) * 100).toFixed(1)}%` : "0%"}</span>
                        </div>
                      </div>
                    ))}

                    {/* Direct payments section — separated by divider */}
                    {(() => {
                      const yearDirectRows = (directResearch || []).filter((d) => d.year === year);
                      if (yearDirectRows.length === 0) return null;
                      const directByCompany = Object.values(
                        yearDirectRows.reduce((acc, d) => {
                          if (!acc[d.companyId]) acc[d.companyId] = { companyName: d.companyName, total: 0 };
                          acc[d.companyId].total += d.totalAmount;
                          return acc;
                        }, {})
                      ).sort((a, b) => b.total - a.total);
                      return (
                        <>
                          <div className="mt-2 pt-2 border-t border-white/10">
                            <p className="text-amber-400/60 text-xs mb-1">Direct payments</p>
                            {directByCompany.map((d) => (
                              <div key={d.companyName} className="flex items-center justify-between gap-3 mt-1">
                                <div className="flex items-center gap-1.5 min-w-0">
                                  <div className="w-2 h-2 rounded-sm shrink-0 bg-amber-500/70" />
                                  <span className="text-white/40 truncate" style={{ maxWidth: "90px" }}>
                                    {d.companyName.length > 18 ? d.companyName.slice(0, 18) + "…" : d.companyName}
                                  </span>
                                </div>
                                <span className="text-amber-400 shrink-0">{formatCurrency(d.total)}</span>
                              </div>
                            ))}
                          </div>
                        </>
                      );
                    })()}
                  </div>
                )}

                {/* Value label above bar */}
                {hasData && chartView === "absolute" && (
                  <div className="absolute w-full text-center pointer-events-none"
                    style={{ bottom: `${Math.max(heightPct, 3)}%`, paddingBottom: "3px" }}>
                    <span className="text-teal-400/80 font-medium" style={{ fontSize: "9px" }}>
                      {total >= 1000000 ? `$${(total / 1000000).toFixed(1)}M`
                        : total >= 1000 ? `$${(total / 1000).toFixed(0)}K`
                        : `$${total.toFixed(0)}`}
                    </span>
                  </div>
                )}

                {/* Stacked bar */}
                <div
                  className="w-full rounded-t overflow-hidden flex flex-col-reverse"
                  style={{ height: hasData ? (chartView === "normalized" ? "calc(100% - 24px)" : `${Math.max(heightPct, 3)}%`) : "3px" }}
                >
                  {(chartView === "normalized" ? normalized : segments).map(({ company, value, pct }) =>
                    value > 0 ? (
                      <div key={company}
                        style={{
                          height: chartView === "normalized" ? `${pct}%` : `${(value / total) * 100}%`,
                          backgroundColor: companyColorMap.get(company),
                          minHeight: "2px",
                        }}
                      />
                    ) : null
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Year labels */}
        <div
          className="absolute bottom-0 left-0 right-0 flex gap-1.5"
          style={{ paddingLeft: "44px", paddingRight: hasDirectData ? "44px" : "0px" }}
        >
          {ALL_YEARS.map((y) => (
            <div key={y} className="flex-1 text-center">
              <span className={y === hoveredYear ? "text-teal-400" : "text-white/25"}
                style={{ fontSize: "10px" }}>{y}</span>
            </div>
          ))}
        </div>

        {/* Direct payments dashed line overlay — rendered last so it sits above bars */}
        {hasDirectData && (
          <svg
            className="absolute inset-0 w-full h-full pointer-events-none"
            style={{ paddingBottom: "24px", paddingTop: "8px", paddingLeft: "44px", paddingRight: "44px", zIndex: 10 }}
          >
            <polyline
              points={directByYear
                .map((d, i) => {
                  if (d.total === 0) return null;
                  const x = ((i + 0.5) / ALL_YEARS.length) * 100;
                  const y = 100 - (d.total / maxDirect) * 85;
                  return `${x}%,${y}%`;
                })
                .filter(Boolean)
                .join(" ")}
              fill="none"
              stroke="rgba(251,191,36,0.9)"
              strokeWidth="2"
              strokeDasharray="4 2"
            />
            {directByYear.map((d, i) => {
              if (d.total === 0) return null;
              const x = ((i + 0.5) / ALL_YEARS.length) * 100;
              const y = 100 - (d.total / maxDirect) * 85;
              return (
                <circle
                  key={i}
                  cx={`${x}%`}
                  cy={`${y}%`}
                  r="4"
                  fill="rgba(251,191,36,1)"
                  stroke="rgba(251,191,36,0.4)"
                  strokeWidth="2"
                />
              );
            })}
          </svg>
        )}
      </div>

      {/* Dual axis legend — only when direct data exists */}
      {hasDirectData && (
        <div className="flex items-center gap-4 mt-2 mb-1 flex-wrap">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-sm bg-teal-500/70 shrink-0" />
            <span className="text-teal-400/70 text-xs">Associated research (left axis)</span>
          </div>
          <div className="flex items-center gap-2">
            <svg width="20" height="10">
              <line x1="0" y1="5" x2="20" y2="5" stroke="rgba(251,191,36,0.6)" strokeWidth="1.5" strokeDasharray="4 2" />
              <circle cx="10" cy="5" r="2.5" fill="rgba(251,191,36,0.8)" />
            </svg>
            <span className="text-amber-400/70 text-xs">Direct research payments (right axis)</span>
          </div>
        </div>
      )}

      {/* Legend */}
      {visibleCompanies.length > 1 && (
        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3">
          {visibleCompanies.slice(0, 8).map((c) => (
            <div key={c} className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-sm shrink-0"
                style={{ backgroundColor: companyColorMap.get(c), opacity: 0.8 }} />
              <span className="text-white/40 text-xs truncate max-w-32">
                {c.length > 25 ? c.slice(0, 25) + "..." : c}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Export */}
      <div className="mt-3 flex items-center justify-between flex-wrap gap-2">
        <button
          onClick={() => exportResearchDetailCsv(detail, name, npi, directResearch, EXPORT_LIMIT)}
          className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-white/10 text-white/40 hover:text-white/70 hover:border-white/20 transition-all"
        >
          ⬇ Export to CSV
        </button>
        <div className="flex items-center gap-3 ml-auto">
          {companies.length > 10 && isAll && (
            <p className="text-white/20 text-xs">
              Showing top 8 of {totalCompanyCount ?? companies.length} companies · export or use filter for all.
            </p>
          )}
          <div className="text-right">
            <p className="text-teal-400 text-sm font-mono font-medium">
              {filteredTotal >= 1000000
                ? `$${(filteredTotal / 1000000).toFixed(2)}M`
                : formatCurrency(filteredTotal)}
            </p>
            <p className="text-white/25 text-xs">
              {isAll ? "total (top 10)" : selectedCompanies.size === 1 ? `total · ${[...selectedCompanies][0].length > 20 ? [...selectedCompanies][0].slice(0, 20) + "…" : [...selectedCompanies][0]}` : `total · ${selectedCompanies.size} companies`}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Context of Research Donut ─────────────────────────────────────────────────
function ResearchContextDonut({ research }) {
  const [hoveredCat, setHoveredCat] = useState(null);

  const CONTEXT_COLORS = {
    "Clinical Trial": "#14b8a6",
    "Basic Research": "#8b5cf6",
    "Expanded Access": "#3b82f6",
    "Other": "#6b7280",
  };

  // Aggregate by context
  const contextTotals = {};
  for (const r of research) {
    const ctx = r.primaryContext || "Other";
    contextTotals[ctx] = (contextTotals[ctx] || 0) + r.totalAmount;
  }

  const total = Object.values(contextTotals).reduce((s, v) => s + v, 0);
  if (total === 0) return null;

  const segments = Object.entries(contextTotals)
    .sort((a, b) => b[1] - a[1])
    .map(([cat, value]) => ({
      cat,
      value,
      pct: (value / total) * 100,
      color: CONTEXT_COLORS[cat] || CONTEXT_COLORS["Other"],
    }));

  const SIZE = 160;
  const CX = 80;
  const CY = 80;
  const R = 62;
  const INNER_R = 38;
  const GAP = 1.5;

  function polarToCartesian(cx, cy, r, angleDeg) {
    const rad = ((angleDeg - 90) * Math.PI) / 180;
    return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
  }

  function arcPath(cx, cy, r, innerR, startDeg, endDeg) {
    const s = polarToCartesian(cx, cy, r, startDeg + GAP / 2);
    const e = polarToCartesian(cx, cy, r, endDeg - GAP / 2);
    const si = polarToCartesian(cx, cy, innerR, startDeg + GAP / 2);
    const ei = polarToCartesian(cx, cy, innerR, endDeg - GAP / 2);
    const large = endDeg - startDeg > 180 ? 1 : 0;
    return [`M ${s.x} ${s.y}`, `A ${r} ${r} 0 ${large} 1 ${e.x} ${e.y}`,
      `L ${ei.x} ${ei.y}`, `A ${innerR} ${innerR} 0 ${large} 0 ${si.x} ${si.y}`, "Z"].join(" ");
  }

  let currentAngle = 0;
  const arcs = segments.map((seg) => {
    const sweep = (seg.pct / 100) * 360;
    const start = currentAngle;
    const end = currentAngle + sweep;
    currentAngle = end;
    return { ...seg, start, end };
  });

  const displaySeg = arcs.find((a) => a.cat === hoveredCat) || null;

  // If all contexts are null/Other, don't render
  if (segments.length === 1 && segments[0].cat === "Other") return null;

  return (
    <div className="card mb-6">
      <h2 className="text-white/60 text-xs uppercase tracking-widest mb-4">
        Research context
      </h2>
      <div className="flex items-start gap-6">
        <div className="shrink-0" style={{ width: SIZE, height: SIZE }}>
          <svg width={SIZE} height={SIZE}>
            {arcs.map((arc) => (
              <path key={arc.cat}
                d={arcPath(CX, CY, R, INNER_R, arc.start, arc.end)}
                fill={arc.color}
                opacity={hoveredCat === null || hoveredCat === arc.cat ? 0.9 : 0.25}
                style={{ cursor: "pointer", transition: "opacity 0.15s" }}
                onMouseEnter={() => setHoveredCat(arc.cat)}
                onMouseLeave={() => setHoveredCat(null)}
              />
            ))}
            {displaySeg ? (
              <>
                <text x={CX} y={CY - 10} textAnchor="middle" fill="white" fontSize="8" opacity={0.45}>
                  {displaySeg.cat.length > 14 ? displaySeg.cat.slice(0, 14) + "…" : displaySeg.cat}
                </text>
                <text x={CX} y={CY + 6} textAnchor="middle" fill="#14b8a6" fontSize="11" fontWeight="600">
                  {displaySeg.pct.toFixed(1)}%
                </text>
              </>
            ) : (
              <>
                <text x={CX} y={CY - 8} textAnchor="middle" fill="white" fontSize="8" opacity={0.45}>
                  Total
                </text>
                <text x={CX} y={CY + 6} textAnchor="middle" fill="#14b8a6" fontSize="11" fontWeight="600">
                  {total >= 1000000 ? `$${(total / 1000000).toFixed(1)}M`
                    : total >= 1000 ? `$${(total / 1000).toFixed(0)}K`
                    : formatCurrency(total)}
                </text>
              </>
            )}
          </svg>
        </div>
        <div className="flex flex-col gap-2.5 flex-1 min-w-0 pt-1">
          {segments.map((seg) => (
            <div key={seg.cat}
              className="flex items-center justify-between gap-3 cursor-pointer"
              onMouseEnter={() => setHoveredCat(seg.cat)}
              onMouseLeave={() => setHoveredCat(null)}
              style={{ opacity: hoveredCat === null || hoveredCat === seg.cat ? 1 : 0.35, transition: "opacity 0.15s" }}
            >
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: seg.color }} />
                <span className="text-white/60 text-xs truncate">{seg.cat}</span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-white/50 text-xs font-mono">{formatCurrency(seg.value)}</span>
                <span className="text-white/25 text-xs w-10 text-right">{seg.pct.toFixed(1)}%</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Product & Device Exposure Chart ──────────────────────────────────────────
function ProductExposureChart({ products: rawProducts, name, npi }) {
  const [showAllProducts, setShowAllProducts] = useState(false);
  const [selectedCompanies, setSelectedCompanies] = useState(new Set(["ALL"]));

  const PRODUCT_TYPE_COLORS = {
    "Drug":           { bar: "bg-purple-500/70", text: "text-purple-300", badge: "bg-purple-500/20 border-purple-500/30 text-purple-300" },
    "Biological":     { bar: "bg-teal-500/70",   text: "text-teal-300",   badge: "bg-teal-500/20 border-teal-500/30 text-teal-300" },
    "Device":         { bar: "bg-blue-500/70",   text: "text-blue-300",   badge: "bg-blue-500/20 border-blue-500/30 text-blue-300" },
    "Medical Supply": { bar: "bg-green-500/70",  text: "text-green-300",  badge: "bg-green-500/20 border-green-500/30 text-green-300" },
  };

  // Companies for filter dropdown — sorted by total funding desc
  const companies = [...new Set((rawProducts || []).map((p) => p.companyName))]
    .filter(Boolean)
    .sort((a, b) => {
      const aVal = (rawProducts || []).filter((p) => p.companyName === a).reduce((s, p) => s + p.totalAmount, 0);
      const bVal = (rawProducts || []).filter((p) => p.companyName === b).reduce((s, p) => s + p.totalAmount, 0);
      return bVal - aVal;
    });

  const isAll = selectedCompanies.has("ALL");

  function toggleCompany(companyName) {
    if (companyName === "ALL") { setSelectedCompanies(new Set(["ALL"])); return; }
    const next = new Set(selectedCompanies);
    next.delete("ALL");
    if (next.has(companyName)) {
      next.delete(companyName);
      if (next.size === 0) next.add("ALL");
    } else {
      next.add(companyName);
    }
    setSelectedCompanies(next);
  }

  // Filter product × company rows by selected companies, then re-aggregate by product
  const filteredRows = isAll
    ? (rawProducts || [])
    : (rawProducts || []).filter((p) => selectedCompanies.has(p.companyName));

  const productMap = {};
  for (const row of filteredRows) {
    const key = row.productName.trim().toUpperCase();
    if (!productMap[key]) {
      productMap[key] = {
        name: row.productName.trim(),
        type: row.productType || "Other",
        totalAmount: 0,
        paymentCount: 0,
        companies: new Set(),
        firstYear: row.firstYear,
        latestYear: row.latestYear,
      };
    }
    productMap[key].totalAmount += row.totalAmount;
    productMap[key].paymentCount += row.paymentCount;
    productMap[key].companies.add(row.companyName);
    productMap[key].firstYear = Math.min(productMap[key].firstYear, row.firstYear);
    productMap[key].latestYear = Math.max(productMap[key].latestYear, row.latestYear);
  }

  const allProducts = Object.values(productMap)
    .map((p) => ({ ...p, companyCount: p.companies.size }))
    .sort((a, b) => b.totalAmount - a.totalAmount);

  const visibleProducts = showAllProducts ? allProducts : allProducts.slice(0, 8);

  if (allProducts.length === 0) return null;

  const maxAmount = Math.max(...allProducts.map((p) => p.totalAmount), 1);

  return (
    <div className="card mb-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-white/60 text-xs uppercase tracking-widest">
          Product &amp; device exposure
        </h2>
        {companies.length > 1 && (
          <ResearchCompanyDropdown
            companies={companies}
            selectedCompanies={selectedCompanies}
            onToggle={toggleCompany}
          />
        )}
      </div>

      <div className="space-y-3">
        {visibleProducts.map((product, i) => {
          const pct = (product.totalAmount / maxAmount) * 100;
          const colors = PRODUCT_TYPE_COLORS[product.type] || {
            bar: "bg-white/20", text: "text-white/50", badge: "bg-white/10 border-white/20 text-white/40",
          };
          return (
            <div key={i}>
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-white/70 text-xs truncate max-w-48">{product.name}</span>
                  {product.type && product.type !== "Other" && (
                    <span className={`text-xs px-1.5 py-0.5 rounded-full border shrink-0 ${colors.badge}`}
                      style={{ fontSize: "10px" }}>
                      {product.type}
                    </span>
                  )}
                  {product.companyCount > 1 && (
                    <span className="text-xs px-1.5 py-0.5 rounded-full border shrink-0 bg-white/5 border-white/10 text-white/30"
                      style={{ fontSize: "10px" }}>
                      {product.companyCount} companies
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3 shrink-0 ml-3">
                  <span className="text-white/30 text-xs">
                    {product.paymentCount} {product.paymentCount === 1 ? "payment" : "payments"}
                  </span>
                  {product.firstYear && (
                    <span className="text-white/20 text-xs">
                      {product.firstYear === product.latestYear
                        ? product.firstYear
                        : `${product.firstYear}–${product.latestYear}`}
                    </span>
                  )}
                  <span className={`text-xs font-mono w-20 text-right ${colors.text}`}>
                    {formatCurrency(product.totalAmount)}
                  </span>
                </div>
              </div>
              <div className="w-full bg-white/5 rounded-full h-2">
                <div className={`h-2 rounded-full ${colors.bar}`} style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        })}
      </div>

      {allProducts.length > 8 && (
        <button
          onClick={() => setShowAllProducts(!showAllProducts)}
          className="mt-4 flex items-center gap-1.5 text-xs text-teal-400/60 hover:text-teal-400 transition-colors"
        >
          <span>{showAllProducts ? "▲" : "▼"}</span>
          <span>
            {showAllProducts
              ? "Show top 8 only"
              : `Show all ${allProducts.length} products`}
          </span>
        </button>
      )}

      <div className="flex items-center justify-between mt-3">
        <button
          onClick={() => exportProductExposureCsv(allProducts, name, npi)}
          className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-white/10 text-white/40 hover:text-white/70 hover:border-white/20 transition-all"
        >
          ⬇ Export to CSV
        </button>
        <p className="text-white/20 text-xs text-right">
          {showAllProducts
            ? `All ${allProducts.length} products · excludes unlabeled records`
            : `Top 8 of ${allProducts.length} products · excludes unlabeled records`}
        </p>
      </div>
    </div>
  );
}

// ── Preclinical vs Clinical Split ─────────────────────────────────────────────
// ── Option 2: Direct Payment Breakdown Card ───────────────────────────────────
function DirectPaymentBreakdownCard({ directResearch }) {
  if (!directResearch || directResearch.length === 0) return null;

  // Group by form_of_payment
  const byForm = directResearch.reduce((acc, d) => {
    const key = d.formOfPayment || "Not reported";
    if (!acc[key]) acc[key] = { form: key, total: 0, count: 0, products: new Set(), studies: new Set() };
    acc[key].total += d.totalAmount;
    acc[key].count += d.paymentCount;
    if (d.productName) acc[key].products.add(d.productName);
    if (d.studyName) acc[key].studies.add(d.studyName);
    return acc;
  }, {});

  const forms = Object.values(byForm).sort((a, b) => b.total - a.total);
  const grandTotal = forms.reduce((s, f) => s + f.total, 0);

  // All unique studies and products
  const allStudies = [...new Set(directResearch.map((d) => d.studyName).filter(Boolean))];
  const allProducts = [...new Set(directResearch.map((d) => d.productName).filter(Boolean))];
  const yearRange = directResearch.length > 0
    ? `${Math.min(...directResearch.map((d) => d.year))}–${Math.max(...directResearch.map((d) => d.year))}`
    : "";

  return (
    <div className="card mb-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-white/60 text-xs uppercase tracking-widest">
          Direct research payments · breakdown
        </h2>
        <span className="text-white/20 text-xs">{yearRange}</span>
      </div>

      {/* Payment form breakdown bars */}
      <div className="space-y-3 mb-4">
        {forms.map((f) => {
          const pct = grandTotal > 0 ? (f.total / grandTotal) * 100 : 0;
          return (
            <div key={f.form}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-white/60 text-xs truncate max-w-[60%]">{f.form}</span>
                <div className="flex items-center gap-2">
                  <span className="text-white/30 text-xs">{pct.toFixed(1)}%</span>
                  <span className="text-amber-400 text-xs font-mono">{formatCurrency(f.total)}</span>
                </div>
              </div>
              <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                <div
                  className="h-full bg-amber-500/60 rounded-full"
                  style={{ width: `${pct}%` }}
                />
              </div>
              <p className="text-white/20 text-xs mt-0.5">
                {f.count} {f.count === 1 ? "payment" : "payments"}
                {f.products.size > 0 && ` · ${[...f.products].slice(0, 2).join(", ")}`}
              </p>
            </div>
          );
        })}
      </div>

      {/* Studies */}
      {allStudies.length > 0 && (
        <div className="pt-3 border-t border-white/5">
          <p className="text-white/30 text-xs mb-1.5">
            Associated {allStudies.length === 1 ? "study" : `studies (${allStudies.length})`}:
          </p>
          <div className="space-y-1">
            {allStudies.slice(0, 3).map((s, i) => (
              <p key={i} className="text-white/40 text-xs line-clamp-1">· {s}</p>
            ))}
            {allStudies.length > 3 && (
              <p className="text-white/20 text-xs">+{allStudies.length - 3} more</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Option 5: Direct vs Associated Funding Comparison Card ───────────────────
function FundingComparisonCard({ totalResearch, totalDirectResearch, hasDirectResearch, selectedYear }) {
  if (!hasDirectResearch) return null;
  const grand = totalResearch + totalDirectResearch;
  if (grand === 0) return null;
  const associatedPct = (totalResearch / grand) * 100;
  const directPct = (totalDirectResearch / grand) * 100;

  return (
    <div className="card mb-6">
      <h2 className="text-white/60 text-xs uppercase tracking-widest mb-4">
        Research funding · direct vs. associated{selectedYear && selectedYear !== "all" ? ` · ${selectedYear}` : " · all years"}
      </h2>

      {/* Stacked bar */}
      <div className="flex rounded-lg overflow-hidden h-3 mb-4">
        <div
          className="bg-teal-500/70 transition-all"
          style={{ width: `${associatedPct}%` }}
          title={`Associated: ${formatCurrency(totalResearch)}`}
        />
        <div
          className="bg-amber-500/70 transition-all"
          style={{ width: `${directPct}%` }}
          title={`Direct: ${formatCurrency(totalDirectResearch)}`}
        />
      </div>

      {/* Legend */}
      <div className="flex gap-6 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-2.5 h-2.5 rounded-sm bg-teal-500/70 shrink-0" />
            <span className="text-white/50 text-xs">Associated (institutional)</span>
          </div>
          <p className="text-teal-400 text-lg font-mono font-medium">{formatCurrency(totalResearch)}</p>
          <p className="text-white/30 text-xs mt-0.5">{associatedPct.toFixed(1)}% · paid to institution · PI named</p>
        </div>
        <div className="w-px bg-white/5 self-stretch" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-2.5 h-2.5 rounded-sm bg-amber-500/70 shrink-0" />
            <span className="text-white/50 text-xs">Direct (personal)</span>
          </div>
          <p className="text-amber-400 text-lg font-mono font-medium">{formatCurrency(totalDirectResearch)}</p>
          <p className="text-white/30 text-xs mt-0.5">{directPct.toFixed(1)}% · paid to physician</p>
        </div>
      </div>
    </div>
  );
}

function PreclinicalSplit({ research, studies, dualInterest, directResearch, name, npi }) {
  const [expanded, setExpanded] = useState(false);

  let clinical = 0, preclinical = 0, unknown = 0;
  let clinicalAmount = 0, preclinicalAmount = 0, unknownAmount = 0;

  for (const s of studies) {
    if (s.preclinicalIndicator === "Yes") {
      preclinical += 1;
      preclinicalAmount += s.totalAmount || 0;
    } else if (s.preclinicalIndicator === "No") {
      clinical += 1;
      clinicalAmount += s.totalAmount || 0;
    } else {
      unknown += 1;
      unknownAmount += s.totalAmount || 0;
    }
  }

  const totalAmount = clinicalAmount + preclinicalAmount + unknownAmount;
  if (clinical === 0 && preclinical === 0) return null;

  // Direct research preclinical split
  const directClinical = (directResearch || []).filter((d) => d.isPreclinical === false);
  const directPreclinical = (directResearch || []).filter((d) => d.isPreclinical === true);
  const directClinicalAmount = directClinical.reduce((s, d) => s + d.totalAmount, 0);
  const directPreclinicalAmount = directPreclinical.reduce((s, d) => s + d.totalAmount, 0);
  const directTotal = directClinicalAmount + directPreclinicalAmount;
  const hasDirectSplit = directTotal > 0 && (directClinicalAmount > 0 || directPreclinicalAmount > 0);
  const directClinicalPct = directTotal > 0 ? (directClinicalAmount / directTotal) * 100 : 0;
  const directPreclinicalPct = directTotal > 0 ? (directPreclinicalAmount / directTotal) * 100 : 0;

  const clinicalPct  = totalAmount > 0 ? (clinicalAmount / totalAmount) * 100 : 0;
  const preclinicalPct = totalAmount > 0 ? (preclinicalAmount / totalAmount) * 100 : 0;
  const unknownPct   = totalAmount > 0 ? (unknownAmount / totalAmount) * 100 : 0;

  // Build per-company breakdown from studies
  const companyMap = {};
  for (const s of studies) {
    if (!s.companyName) continue;
    if (!companyMap[s.companyId]) {
      companyMap[s.companyId] = {
        companyId: s.companyId,
        companyName: s.companyName,
        clinical: 0, preclinical: 0, unknown: 0,
        clinicalAmount: 0, preclinicalAmount: 0, unknownAmount: 0,
      };
    }
    const c = companyMap[s.companyId];
    if (s.preclinicalIndicator === "Yes") {
      c.preclinical += 1;
      c.preclinicalAmount += s.totalAmount || 0;
    } else if (s.preclinicalIndicator === "No") {
      c.clinical += 1;
      c.clinicalAmount += s.totalAmount || 0;
    } else {
      c.unknown += 1;
      c.unknownAmount += s.totalAmount || 0;
    }
  }

  const companies = Object.values(companyMap)
    .sort((a, b) => (b.clinicalAmount + b.preclinicalAmount + b.unknownAmount) -
                    (a.clinicalAmount + a.preclinicalAmount + a.unknownAmount));

  // Build direct payment company breakdown
  const directCompanyMap = {};
  for (const d of (directResearch || [])) {
    if (!d.companyName) continue;
    if (!directCompanyMap[d.companyId]) {
      directCompanyMap[d.companyId] = {
        companyId: d.companyId,
        companyName: d.companyName,
        total: 0,
        paymentCount: 0,
        firstYear: d.year,
        lastYear: d.year,
        isPreclinical: false,
      };
    }
    const c = directCompanyMap[d.companyId];
    c.total += d.totalAmount;
    c.paymentCount += d.paymentCount;
    c.firstYear = Math.min(c.firstYear, d.year);
    c.lastYear = Math.max(c.lastYear, d.year);
    if (d.isPreclinical) c.isPreclinical = true;
  }
  const directCompanies = Object.values(directCompanyMap)
    .sort((a, b) => b.total - a.total);

  return (
    <div className="card mb-6">
      <h2 className="text-white/60 text-xs uppercase tracking-widest mb-4">
        Preclinical vs. clinical research · associated
      </h2>

      {/* Split bar */}
      <div className="flex rounded-lg overflow-hidden h-3 mb-4">
        {clinicalAmount > 0 && (
          <div className="bg-teal-500/70 transition-all" style={{ width: `${clinicalPct}%` }} />
        )}
        {preclinicalAmount > 0 && (
          <div className="bg-violet-500/70 transition-all" style={{ width: `${preclinicalPct}%` }} />
        )}
        {unknownAmount > 0 && (
          <div className="bg-white/15 transition-all" style={{ width: `${unknownPct}%` }} />
        )}
      </div>

      {/* Stats row */}
      <div className="flex gap-4 flex-wrap">
        {clinicalAmount > 0 && (
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-2.5 h-2.5 rounded-sm bg-teal-500/70 shrink-0" />
              <span className="text-white/50 text-xs">Clinical ({clinical} payments)</span>
            </div>
            <p className="text-teal-400 text-lg font-mono font-medium">{formatCurrency(clinicalAmount)}</p>
            <p className="text-white/30 text-xs mt-0.5">{clinicalPct.toFixed(1)}% of research funding</p>
          </div>
        )}
        {clinicalAmount > 0 && preclinicalAmount > 0 && <div className="w-px bg-white/5 self-stretch" />}
        {preclinicalAmount > 0 && (
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-2.5 h-2.5 rounded-sm bg-violet-500/70 shrink-0" />
              <span className="text-white/50 text-xs">Preclinical ({preclinical} payments)</span>
            </div>
            <p className="text-violet-400 text-lg font-mono font-medium">{formatCurrency(preclinicalAmount)}</p>
            <p className="text-white/30 text-xs mt-0.5">{preclinicalPct.toFixed(1)}% of research funding</p>
          </div>
        )}
        {unknownAmount > 0 && (clinicalAmount > 0 || preclinicalAmount > 0) && <div className="w-px bg-white/5 self-stretch" />}
        {unknownAmount > 0 && (
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-2.5 h-2.5 rounded-sm bg-white/15 shrink-0" />
              <span className="text-white/50 text-xs">Not reported ({unknown} payments)</span>
            </div>
            <p className="text-white/40 text-lg font-mono font-medium">{formatCurrency(unknownAmount)}</p>
            <p className="text-white/30 text-xs mt-0.5">{unknownPct.toFixed(1)}% of research funding</p>
          </div>
        )}
      </div>

      {/* Direct research preclinical split — Option 3 */}
      {hasDirectSplit && (
        <div className="mt-4 pt-3 border-t border-white/5">
          <p className="text-white/30 text-xs mb-2 uppercase tracking-widest">Direct research payments</p>
          <div className="flex rounded-lg overflow-hidden h-2 mb-3">
            {directClinicalAmount > 0 && (
              <div className="bg-amber-500/60 transition-all" style={{ width: `${directClinicalPct}%` }} />
            )}
            {directPreclinicalAmount > 0 && (
              <div className="bg-violet-500/40 transition-all" style={{ width: `${directPreclinicalPct}%` }} />
            )}
          </div>
          <div className="flex gap-4 flex-wrap">
            {directClinicalAmount > 0 && (
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-2 h-2 rounded-sm bg-amber-500/60 shrink-0" />
                  <span className="text-white/40 text-xs">Clinical · direct</span>
                </div>
                <p className="text-amber-400 text-sm font-mono">{formatCurrency(directClinicalAmount)}</p>
                <p className="text-white/20 text-xs">{directClinicalPct.toFixed(1)}% · {directClinical.reduce((s,d)=>s+d.paymentCount,0)} payments</p>
              </div>
            )}
            {directPreclinicalAmount > 0 && (
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-2 h-2 rounded-sm bg-violet-500/40 shrink-0" />
                  <span className="text-white/40 text-xs">Preclinical · direct</span>
                </div>
                <p className="text-violet-400 text-sm font-mono">{formatCurrency(directPreclinicalAmount)}</p>
                <p className="text-white/20 text-xs">{directPreclinicalPct.toFixed(1)}% · {directPreclinical.reduce((s,d)=>s+d.paymentCount,0)} payments</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Company breakdown toggle */}
      {(companies.length > 0 || directCompanies.length > 0) && (
        <div className="mt-4 pt-3 border-t border-white/5">
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-2 text-xs text-white/40 hover:text-white/70 transition-colors"
          >
            <span>{expanded ? "▲" : "▼"}</span>
            <span>{expanded ? "Hide" : "Show"} company breakdown</span>
          </button>

          {expanded && (
            <div className="mt-3 space-y-4">

              {/* Direct payment companies */}
              {directCompanies.length > 0 && (
                <div>
                  <p className="text-amber-400/60 text-xs uppercase tracking-widest mb-2">Direct payments</p>
                  <div className="space-y-2">
                    {directCompanies.map((c) => {
                      const isDual = dualInterest?.some((d) => d.companyId === c.companyId);
                      return (
                        <div key={c.companyId}
                          className="flex items-start justify-between gap-3 bg-amber-500/[0.03] border border-amber-500/15 rounded-lg px-3 py-2.5">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="text-white/70 text-xs font-medium truncate">{c.companyName}</p>
                              {c.isPreclinical && (
                                <span className="text-xs px-1.5 py-0.5 rounded-full border bg-violet-500/20 border-violet-500/30 text-violet-300 shrink-0"
                                  style={{ fontSize: "10px" }}>
                                  Preclinical
                                </span>
                              )}
                              {isDual && (
                                <span className="text-xs px-1.5 py-0.5 rounded-full border bg-teal-500/20 border-teal-500/30 text-teal-400 shrink-0"
                                  style={{ fontSize: "10px" }}>
                                  ⚗ Ownership conflict
                                </span>
                              )}
                            </div>
                            <p className="text-white/30 text-xs mt-0.5">
                              {c.firstYear === c.lastYear ? `${c.firstYear}` : `${c.firstYear}–${c.lastYear}`}
                              {" · "}{c.paymentCount} {c.paymentCount === 1 ? "payment" : "payments"}
                              {" · "}paid directly to physician
                            </p>
                          </div>
                          <p className="text-amber-400 text-xs font-mono shrink-0">{formatCurrency(c.total)}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Associated research companies */}
              {companies.length > 0 && (
                <div>
                  {directCompanies.length > 0 && (
                    <p className="text-teal-400/60 text-xs uppercase tracking-widest mb-2">Associated research</p>
                  )}
                  <div className="space-y-2">
              {companies.map((c) => {
                const compTotal = c.clinicalAmount + c.preclinicalAmount + c.unknownAmount;
                const isDual = dualInterest?.some((d) => d.companyId === c.companyId);
                const hasPreclinical = c.preclinical > 0;
                const hasClinical = c.clinical > 0;
                const type = hasPreclinical && hasClinical ? "Mixed"
                  : hasPreclinical ? "Preclinical"
                  : "Clinical";
                const typeColor = type === "Preclinical" ? "bg-violet-500/20 border-violet-500/30 text-violet-300"
                  : type === "Mixed" ? "bg-amber-500/20 border-amber-500/30 text-amber-300"
                  : "bg-teal-500/20 border-teal-500/30 text-teal-300";

                return (
                  <div key={c.companyId}
                    className="flex items-start justify-between gap-3 bg-white/[0.02] border border-white/[0.06] rounded-lg px-3 py-2.5">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-white/70 text-xs font-medium truncate">{c.companyName}</p>
                        <span className={`text-xs px-1.5 py-0.5 rounded-full border shrink-0 ${typeColor}`}
                          style={{ fontSize: "10px" }}>
                          {type}
                        </span>
                        {isDual && (
                          <span className="text-xs px-1.5 py-0.5 rounded-full border bg-teal-500/20 border-teal-500/30 text-teal-400 shrink-0"
                            style={{ fontSize: "10px" }}>
                            ⚗ Ownership conflict
                          </span>
                        )}
                      </div>
                      <div className="flex gap-3 mt-1 flex-wrap">
                        {c.clinical > 0 && (
                          <span className="text-white/30 text-xs">
                            Clinical: {c.clinical} payment{c.clinical !== 1 ? "s" : ""}
                          </span>
                        )}
                        {c.preclinical > 0 && (
                          <span className="text-white/30 text-xs">
                            Preclinical: {c.preclinical} payment{c.preclinical !== 1 ? "s" : ""}
                          </span>
                        )}
                      </div>
                    </div>
                    <p className="text-teal-400 text-xs font-mono shrink-0">{formatCurrency(compTotal)}</p>
                  </div>
                );
              })}
                  </div>
                </div>
              )}

            </div>
          )}

          {!expanded && (
            <p className="text-white/20 text-xs mt-3">
              ⚗ Preclinical involvement with a company in which the provider holds equity may indicate early-stage financial relationships predating commercialization.
            </p>
          )}
        </div>
      )}

      {/* Export */}
      <div className="mt-3 pt-3 border-t border-white/5">
        <button
          onClick={() => exportPreclinicalCsv(companies, name, npi)}
          className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-white/10 text-white/40 hover:text-white/70 hover:border-white/20 transition-all"
        >
          ⬇ Export to CSV
        </button>
      </div>
    </div>
  );
}

// ── Expenditure Category Chart ────────────────────────────────────────────────
function ExpenditureChart({ detail }) {
  const catTotals = {};
  for (const d of detail) {
    if (
      d.expenditureCategory1 &&
      d.expenditureCategory1.trim() !== "" &&
      d.expenditureCategory1.toUpperCase().trim() !== "OTHER"
    ) {
      catTotals[d.expenditureCategory1] = (catTotals[d.expenditureCategory1] || 0) + d.totalAmount;
    }
  }

  const total = Object.values(catTotals).reduce((s, v) => s + v, 0);
  if (total === 0) return null;

  const categories = Object.entries(catTotals)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8);

  const COLORS = [
    "bg-teal-500/70", "bg-violet-500/70", "bg-amber-400/70",
    "bg-blue-500/70", "bg-red-500/70", "bg-orange-500/70",
    "bg-green-500/70", "bg-pink-500/70",
  ];

  return (
    <div className="card mb-6">
      <h2 className="text-white/60 text-xs uppercase tracking-widest mb-4">
        Expenditure categories
      </h2>
      <div className="space-y-3">
        {categories.map(([cat, value], i) => {
          const pct = (value / total) * 100;
          return (
            <div key={cat}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-white/60 text-xs truncate max-w-xs">{cat}</span>
                <div className="flex items-center gap-3 shrink-0 ml-3">
                  <span className="text-white/40 text-xs font-mono">{pct.toFixed(1)}%</span>
                  <span className="text-teal-400/80 text-xs font-mono w-24 text-right">
                    {formatCurrency(value)}
                  </span>
                </div>
              </div>
              <div className="w-full bg-white/5 rounded-full h-2">
                <div className={`h-2 rounded-full ${COLORS[i % COLORS.length]}`}
                  style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-white/20 text-xs mt-4 text-right">
        Based on {formatCurrency(total)} total research funding
      </p>
    </div>
  );
}

// ── Research vs Ownership Dual-Axis Chart ─────────────────────────────────────
function ResearchOwnershipDualAxisChart({ detail, ownershipDetail, dualInterest }) {
  const years = ALL_YEARS;
  const [hoveredYear, setHoveredYear] = useState(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState(
    dualInterest.length > 0 ? dualInterest[0].companyId : null
  );

  useEffect(() => {
    if (dualInterest.length > 0 && !selectedCompanyId) {
      setSelectedCompanyId(dualInterest[0].companyId);
    }
  }, [dualInterest]);

  if (dualInterest.length === 0) return null;

  const selectedDual = dualInterest.find((d) => d.companyId === selectedCompanyId) || dualInterest[0];

  // Research funding per year for selected company
  const researchByYear = years.map((y) => ({
    year: y,
    value: detail
      .filter((d) => d.programYear === y && d.companyId === selectedCompanyId)
      .reduce((s, r) => s + r.totalAmount, 0),
  }));

  // Ownership value per year for selected company
  const ownershipByYear = years.map((y) => ({
    year: y,
    value: ownershipDetail
      .filter((o) => o.programYear === y && o.companyId === selectedCompanyId)
      .reduce((s, o) => s + o.valueOfInterest, 0),
  }));

  const maxResearch = Math.max(...researchByYear.map((y) => y.value), 1);
  const maxOwnership = Math.max(...ownershipByYear.map((y) => y.value), 1);

  const hasAnyData =
    researchByYear.some((y) => y.value > 0) ||
    ownershipByYear.some((y) => y.value > 0);

  if (!hasAnyData) return null;

  const hoveredResearch = hoveredYear ? researchByYear.find((y) => y.year === hoveredYear) : null;
  const hoveredOwnership = hoveredYear ? ownershipByYear.find((y) => y.year === hoveredYear) : null;

  return (
    <div className="card mb-6">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div>
          <h2 className="text-white/60 text-xs uppercase tracking-widest">
            Dual Interest: Research funding vs. ownership value
          </h2>
          <p className="text-white/25 text-xs mt-1">
            Companies where this provider received research funding and holds or held an ownership interest
          </p>
        </div>
        {hoveredYear && (
          <div className="text-right shrink-0">
            <p className="text-white/40 text-xs font-medium">{hoveredYear}</p>
            {hoveredResearch?.value > 0 ? (
              <>
                <p className="text-teal-400 text-xs font-mono">{formatCurrency(hoveredResearch.value)}</p>
                <p className="text-teal-400/50 text-xs">research funding</p>
              </>
            ) : (
              <p className="text-white/20 text-xs">no research</p>
            )}
            {hoveredOwnership?.value > 0 ? (
              <>
                <p className="text-amber-400 text-xs font-mono mt-1">{formatCurrency(hoveredOwnership.value)}</p>
                <p className="text-amber-400/50 text-xs">ownership value</p>
              </>
            ) : (
              <p className="text-white/20 text-xs mt-1">no ownership</p>
            )}
          </div>
        )}
      </div>

      {/* Company selector */}
      <div className="flex flex-wrap gap-1.5 mb-4">
        {dualInterest.length === 1 ? (
          <span className="text-xs px-3 py-1.5 rounded-lg border bg-teal-500/20 border-teal-500/30 text-teal-400">
            {dualInterest[0].companyName.length > 28
              ? dualInterest[0].companyName.slice(0, 28) + "…"
              : dualInterest[0].companyName}
          </span>
        ) : (
          dualInterest.map((d) => (
            <button
              key={d.companyId}
              onClick={() => { setSelectedCompanyId(d.companyId); setHoveredYear(null); }}
              className={`text-xs px-3 py-1.5 rounded-lg border transition-all ${
                selectedCompanyId === d.companyId
                  ? "bg-teal-500/20 border-teal-500/30 text-teal-400"
                  : "border-white/10 text-white/40 hover:border-white/20 hover:text-white/60"
              }`}
            >
              {d.companyName.length > 28 ? d.companyName.slice(0, 28) + "…" : d.companyName}
            </button>
          ))
        )}
      </div>

      {/* Summary cards */}
      <div className="flex gap-3 mb-4 flex-wrap">
        <div className="flex-1 min-w-0 bg-teal-500/5 border border-teal-500/10 rounded-lg px-3 py-2">
          <p className="text-teal-400/60 text-xs">Research period</p>
          <p className="text-teal-400 text-sm font-medium mt-0.5">
            {selectedDual.researchFirstYear === selectedDual.researchLatestYear
              ? selectedDual.researchFirstYear
              : `${selectedDual.researchFirstYear}–${selectedDual.researchLatestYear}`}
          </p>
        </div>
        <div className="flex-1 min-w-0 bg-amber-500/5 border border-amber-500/10 rounded-lg px-3 py-2">
          <p className="text-amber-400/60 text-xs">Ownership period</p>
          <p className="text-amber-400 text-sm font-medium mt-0.5">
            {selectedDual.ownershipFirstYear === selectedDual.ownershipLatestYear
              ? selectedDual.ownershipFirstYear
              : `${selectedDual.ownershipFirstYear}–${selectedDual.ownershipLatestYear}`}
          </p>
        </div>
        {selectedDual.hasDualInterestSamePeriod && (
          <div className="flex items-center px-3 py-2 bg-teal-500/10 border border-teal-500/20 rounded-lg shrink-0">
            <span className="text-teal-400 text-xs">⚗ Same period overlap</span>
          </div>
        )}
      </div>

      {/* Chart */}
      <div className="relative" style={{ height: "180px" }}>
        {/* Left Y-axis — research (teal) */}
        <div className="absolute left-0 top-0 bottom-6 flex flex-col justify-between items-end pr-1 pointer-events-none" style={{ width: "36px" }}>
          {[1, 0.75, 0.5, 0.25, 0].map((pct) => (
            <span key={pct} className="text-teal-400/50" style={{ fontSize: "8px" }}>
              {maxResearch * pct >= 1000000 ? `$${((maxResearch * pct) / 1000000).toFixed(1)}M`
                : maxResearch * pct >= 1000 ? `$${((maxResearch * pct) / 1000).toFixed(0)}K`
                : `$${(maxResearch * pct).toFixed(0)}`}
            </span>
          ))}
        </div>
        {/* Right Y-axis — ownership (amber) */}
        <div className="absolute right-0 top-0 bottom-6 flex flex-col justify-between items-start pl-1 pointer-events-none" style={{ width: "36px" }}>
          {[1, 0.75, 0.5, 0.25, 0].map((pct) => (
            <span key={pct} className="text-amber-400/50" style={{ fontSize: "8px" }}>
              {maxOwnership * pct >= 1000000 ? `$${((maxOwnership * pct) / 1000000).toFixed(1)}M`
                : maxOwnership * pct >= 1000 ? `$${((maxOwnership * pct) / 1000).toFixed(0)}K`
                : `$${(maxOwnership * pct).toFixed(0)}`}
            </span>
          ))}
        </div>

        <div className="absolute inset-0 flex items-end gap-1.5 pb-6" style={{ left: "38px", right: "38px" }}>
          {years.map((y) => {
            const research = researchByYear.find((d) => d.year === y);
            const ownership = ownershipByYear.find((d) => d.year === y);
            const researchPct = research ? (research.value / maxResearch) * 100 : 0;
            const ownershipPct = ownership ? (ownership.value / maxOwnership) * 100 : 0;
            const hasData = researchPct > 0 || ownershipPct > 0;
            const isHovered = hoveredYear === y;

            return (
              <div
                key={y}
                className="flex-1 flex items-end justify-center gap-0.5 h-full relative"
                onMouseEnter={() => setHoveredYear(y)}
                onMouseLeave={() => setHoveredYear(null)}
                style={{ cursor: hasData ? "pointer" : "default" }}
              >
                {/* Research value label */}
                {researchPct > 0 && (
                  <div className="absolute left-0 text-center pointer-events-none"
                    style={{ bottom: `calc(${Math.max(researchPct, 3)}% + 2px)`, width: "calc(50% - 1px)", fontSize: "8px" }}>
                    <span className="text-teal-400/70">
                      {research?.value >= 1000000 ? `$${(research.value / 1000000).toFixed(1)}M`
                        : research?.value >= 1000 ? `$${(research.value / 1000).toFixed(0)}K`
                        : `$${research?.value?.toFixed(0)}`}
                    </span>
                  </div>
                )}
                {/* Ownership value label */}
                {ownershipPct > 0 && (
                  <div className="absolute right-0 text-center pointer-events-none"
                    style={{ bottom: `calc(${Math.max(ownershipPct, 3)}% + 2px)`, width: "calc(50% - 1px)", fontSize: "8px" }}>
                    <span className="text-amber-400/70">
                      {ownership?.value >= 1000000 ? `$${(ownership.value / 1000000).toFixed(1)}M`
                        : ownership?.value >= 1000 ? `$${(ownership.value / 1000).toFixed(0)}K`
                        : `$${ownership?.value?.toFixed(0)}`}
                    </span>
                  </div>
                )}
                {/* Research bar — teal */}
                <div className="flex-1 rounded-t" style={{
                  height: researchPct > 0 ? `${Math.max(researchPct, 3)}%` : "2px",
                  backgroundColor: researchPct > 0
                    ? isHovered ? "rgba(20,184,166,1)" : "rgba(20,184,166,0.7)"
                    : "rgba(255,255,255,0.04)",
                  transition: "background-color 0.15s",
                }} />
                {/* Ownership bar — amber */}
                <div className="flex-1 rounded-t" style={{
                  height: ownershipPct > 0 ? `${Math.max(ownershipPct, 3)}%` : "2px",
                  backgroundColor: ownershipPct > 0
                    ? isHovered ? "rgba(251,191,36,1)" : "rgba(251,191,36,0.7)"
                    : "rgba(255,255,255,0.04)",
                  transition: "background-color 0.15s",
                }} />
              </div>
            );
          })}
        </div>

        {/* Year labels */}
        <div className="absolute bottom-0 flex gap-1.5" style={{ left: "38px", right: "38px" }}>
          {years.map((y) => (
            <div key={y} className="flex-1 text-center">
              <span className={y === hoveredYear ? "text-white/60" : "text-white/25"} style={{ fontSize: "10px" }}>
                {y}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/5">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm bg-teal-400/70" />
            <span className="text-white/40 text-xs">Research funding</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm bg-amber-400/70" />
            <span className="text-white/40 text-xs">Ownership value</span>
          </div>
        </div>
        <p className="text-white/40 text-xs">Each axis scaled independently</p>
      </div>
    </div>
  );
}

// ── Collapsible conflict banner ───────────────────────────────────────────────
function CollapsibleBanner({ color, icon, title, description, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const colorMap = {
    red:   { border: "border-red-500/30",   bg: "bg-red-500/10",   icon: "text-red-400",   title: "text-red-400",   chevron: "text-red-400/60"   },
    amber: { border: "border-amber-500/30", bg: "bg-amber-500/10", icon: "text-amber-400", title: "text-amber-400", chevron: "text-amber-400/60" },
    teal:  { border: "border-teal-500/30",  bg: "bg-teal-500/10",  icon: "text-teal-400",  title: "text-teal-400",  chevron: "text-teal-400/60"  },
    blue:  { border: "border-blue-500/30",  bg: "bg-blue-500/10",  icon: "text-blue-400",  title: "text-blue-400",  chevron: "text-blue-400/60"  },
  };
  const c = colorMap[color] || colorMap.amber;
  return (
    <div className={`${c.bg} border ${c.border} rounded-xl overflow-hidden`}>
      <button onClick={() => setOpen(!open)} className="print:hidden w-full flex items-start gap-2 p-4 text-left">
        <span className={`${c.icon} text-sm mt-0.5 shrink-0`}>{icon}</span>
        <div className="flex-1 min-w-0">
          <p className={`${c.title} font-medium text-sm`}>{title}</p>
          <p className="text-white/50 text-xs mt-1 leading-relaxed">{description}</p>
        </div>
        <span className={`${c.chevron} text-xs mt-0.5 shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`}>▼</span>
      </button>
      <div className="hidden print:flex items-start gap-2 p-4">
        <span className={`${c.icon} text-sm mt-0.5 shrink-0`}>{icon}</span>
        <div className="flex-1 min-w-0">
          <p className={`${c.title} font-medium text-sm`}>{title}</p>
          <p className="text-white/50 text-xs mt-1 leading-relaxed">{description}</p>
        </div>
      </div>
      {open && children && (
        <div className="px-4 pb-4 pt-0 border-t border-white/5">{children}</div>
      )}
      {/* Print: no children — header summary only */}
    </div>
  );
}

function ResearchConflictBanners({
  hasTripleInterest, tripleInterestCompanyIds,
  hasDualInterest, hasDualInterestSamePeriod, dualInterest,
  hasPaymentOwnershipDual, paymentOwnershipDual,
  researchGeneralDual, hasResearchGeneralDual,
  isPro, setUpgradeMessage, setShowUpgradeModal,
}) {
  return (
    <>
      {hasTripleInterest && (
        <CollapsibleBanner
          color="red" icon="⚠"
          title="Triple Interest Conflict"
          description={`This provider has general payments, ownership interests, and research funding from the same ${tripleInterestCompanyIds.size === 1 ? "company" : `${tripleInterestCompanyIds.size} companies`}. This combination carries simultaneous Stark Law, Anti-Kickback Statute, and research integrity exposure.`}
          defaultOpen={false}
        >
          {isPro ? (
            <div className="space-y-2 mt-3">
              {dualInterest.filter((d) => tripleInterestCompanyIds.has(d.companyId)).map((d, i) => {
                const resGen = researchGeneralDual.find((g) => g.companyId === d.companyId);
                const payOwn = paymentOwnershipDual.find((p) => p.companyId === d.companyId);
                return (
                  <div key={i} className="bg-red-500/5 border border-red-500/20 rounded-lg px-3 py-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-red-300 text-xs font-medium">{d.companyName}</p>
                      {payOwn?.hasTripleInterestSameYear && <span className="bg-red-500/30 border border-red-500/50 text-red-200 text-xs px-1.5 py-0.5 rounded-full font-medium">⚑ All three same year</span>}
                    </div>
                    <div className="flex gap-2 flex-wrap mt-1">
                      {payOwn?.hasDualInterestSamePeriod && <span className="bg-red-500/20 border border-red-500/30 text-red-300 text-xs px-1.5 py-0.5 rounded-full">Payments+Ownership overlap</span>}
                      {d.hasDualInterestSamePeriod && <span className="bg-red-500/20 border border-red-500/30 text-red-300 text-xs px-1.5 py-0.5 rounded-full">Research+Ownership overlap</span>}
                      {resGen?.hasDualInterestSamePeriod && <span className="bg-red-500/20 border border-red-500/30 text-red-300 text-xs px-1.5 py-0.5 rounded-full">Research+Payments overlap</span>}
                      {resGen?.highPaymentsPreclinicalResearch && <span className="bg-violet-500/20 border border-violet-500/30 text-violet-300 text-xs px-1.5 py-0.5 rounded-full">Preclinical</span>}
                    </div>
                    <div className="flex gap-4 mt-2 flex-wrap">
                      {payOwn && <div><p className="text-white/40 text-xs font-mono">{formatCurrency(payOwn.generalPaymentsTotal)}</p><p className="text-white/25 text-xs">general payments</p></div>}
                      <div><p className="text-amber-400 text-xs font-mono">{formatCurrency(d.ownershipValue)}</p><p className="text-white/25 text-xs">ownership value</p></div>
                      <div><p className="text-teal-400 text-xs font-mono">{formatCurrency(d.researchTotal)}</p><p className="text-white/25 text-xs">research funding</p></div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <button onClick={() => { setUpgradeMessage("Upgrade to Pro to see full triple interest conflict details."); setShowUpgradeModal(true); }} className="mt-3 text-xs px-3 py-1.5 rounded-lg bg-red-500/20 border border-red-500/30 text-red-300 hover:bg-red-500/30 transition-all">🔒 Upgrade to Pro to see details</button>
          )}
        </CollapsibleBanner>
      )}


      {hasDualInterest && dualInterest.some((d) => !tripleInterestCompanyIds.has(d.companyId)) && (
        <CollapsibleBanner
          color="teal" icon="⚗"
          title="Ownership & Research Conflict"
          description={`This provider conducted research funded by ${dualInterest.filter((d) => !tripleInterestCompanyIds.has(d.companyId)).length === 1 ? "a company" : `${dualInterest.filter((d) => !tripleInterestCompanyIds.has(d.companyId)).length} companies`} in which they also hold or have held an ownership interest.${hasDualInterestSamePeriod ? " Research funding and ownership overlap in the same reporting period." : ""}`}
        >
          {isPro ? (
            <div className="space-y-2 mt-3">
              {dualInterest.filter((d) => !tripleInterestCompanyIds.has(d.companyId)).map((d, i) => (
                <div key={i} className="bg-teal-500/5 border border-teal-500/20 rounded-lg px-3 py-2">
                  <div className="flex justify-between items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-teal-300 text-xs font-medium">{d.companyName}</p>
                        {d.hasDualInterestSamePeriod && <span className="bg-teal-500/20 border border-teal-500/30 text-teal-400 text-xs px-1.5 py-0.5 rounded-full">Same period</span>}
                        {d.hasPreclinical && <span className="bg-violet-500/20 border border-violet-500/30 text-violet-300 text-xs px-1.5 py-0.5 rounded-full">Preclinical</span>}
                      </div>
                      <p className="text-teal-300/50 text-xs mt-0.5">
                        {d.hasDualInterestSamePeriod ? `Research and ownership overlap · ${Math.max(d.researchFirstYear, d.ownershipFirstYear)}–${Math.min(d.researchLatestYear, d.ownershipLatestYear)}` : `Research: ${d.researchFirstYear}–${d.researchLatestYear} · Ownership: ${d.ownershipFirstYear}–${d.ownershipLatestYear}`}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-teal-400 text-xs font-mono font-medium">{formatCurrency(d.researchTotal)}</p>
                      <p className="text-teal-300/40 text-xs">research funding</p>
                      <p className="text-white/40 text-xs font-mono mt-1">{formatCurrency(d.ownershipValue)}</p>
                      <p className="text-white/30 text-xs">ownership value</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <button onClick={() => { setUpgradeMessage("Upgrade to Pro to see full research and ownership conflict details."); setShowUpgradeModal(true); }} className="mt-3 text-xs px-3 py-1.5 rounded-lg bg-teal-500/20 border border-teal-500/30 text-teal-300 hover:bg-teal-500/30 transition-all">🔒 Upgrade to Pro to see details</button>
          )}
        </CollapsibleBanner>
      )}

      {hasResearchGeneralDual && researchGeneralDual.some((g) => !tripleInterestCompanyIds.has(g.companyId)) && (
        <CollapsibleBanner
          color="blue" icon="⚗"
          title="General & Research Conflict"
          description={(() => {
            const uniqueCount = new Set(researchGeneralDual.filter((g) => !tripleInterestCompanyIds.has(g.companyId)).map((g) => g.companyId)).size;
            const nonTriple = researchGeneralDual.filter((g) => !tripleInterestCompanyIds.has(g.companyId));
            return `This provider received general payments and research funding from the same ${uniqueCount === 1 ? "company" : `${uniqueCount} companies`}.${nonTriple.some((g) => g.hasDualInterestSamePeriod) ? " General payments and research overlap in the same reporting period." : ""}${nonTriple.some((g) => g.highPaymentsPreclinicalResearch) ? " High general payments detected during preclinical research." : ""}`;
          })()}
        >
          {isPro ? (
            <div className="space-y-2 mt-3">
              {Object.values(
                researchGeneralDual
                  .filter((g) => !tripleInterestCompanyIds.has(g.companyId))
                  .reduce((acc, g) => {
                    if (!acc[g.companyId]) {
                      acc[g.companyId] = { ...g, paymentTypes: [g.paymentType] };
                    } else {
                      acc[g.companyId].researchTotal += g.researchTotal;
                      acc[g.companyId].paymentTypes.push(g.paymentType);
                      acc[g.companyId].hasDualInterestSamePeriod =
                        acc[g.companyId].hasDualInterestSamePeriod || g.hasDualInterestSamePeriod;
                      acc[g.companyId].highPaymentsPreclinicalResearch =
                        acc[g.companyId].highPaymentsPreclinicalResearch || g.highPaymentsPreclinicalResearch;
                      acc[g.companyId].hasPreclinical =
                        acc[g.companyId].hasPreclinical || g.hasPreclinical;
                    }
                    return acc;
                  }, {})
              ).map((g, i) => (
                <div key={i} className="bg-blue-500/5 border border-blue-500/20 rounded-lg px-3 py-2">
                  <div className="flex justify-between items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-blue-300 text-xs font-medium">{g.companyName}</p>
                        {g.paymentTypes?.includes("direct") && (
                          <span className="bg-amber-500/15 border border-amber-500/30 text-amber-400 text-xs px-1.5 py-0.5 rounded-full">Direct payment</span>
                        )}
                        {g.hasDualInterestSamePeriod && <span className="bg-blue-500/20 border border-blue-500/30 text-blue-300 text-xs px-1.5 py-0.5 rounded-full">Same period</span>}
                        {g.highPaymentsPreclinicalResearch && <span className="bg-violet-500/20 border border-violet-500/30 text-violet-300 text-xs px-1.5 py-0.5 rounded-full">Preclinical</span>}
                      </div>
                      <p className="text-blue-300/50 text-xs mt-0.5">
                        {g.hasDualInterestSamePeriod ? `Research and payments overlap · ${g.overlapFirstYear}–${g.overlapLatestYear}` : `Research: ${g.researchFirstYear}–${g.researchLatestYear} · Payments: ${g.generalPaymentsFirstYear}–${g.generalPaymentsLatestYear}`}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-teal-400 text-xs font-mono font-medium">{formatCurrency(g.researchTotal)}</p>
                      <p className="text-blue-300/40 text-xs">research funding</p>
                      <p className="text-white/40 text-xs font-mono mt-1">{formatCurrency(g.generalPaymentsTotal)}</p>
                      <p className="text-white/30 text-xs">general payments</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <button onClick={() => { setUpgradeMessage("Upgrade to Pro to see full research and general payment conflict details."); setShowUpgradeModal(true); }} className="mt-3 text-xs px-3 py-1.5 rounded-lg bg-blue-500/20 border border-blue-500/30 text-blue-300 hover:bg-blue-500/30 transition-all">🔒 Upgrade to Pro to see details</button>
          )}
        </CollapsibleBanner>
      )}
    </>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function PhysicianResearchPage() {
  const { npi } = useParams();
  const [searchParams] = useSearchParams();
  const name = searchParams.get("name") || "This provider";
  const providerState = searchParams.get("state") || "";
  const specialty = searchParams.get("specialty") || "";
  const totalPayments = parseFloat(searchParams.get("totalPayments") || "0");

  const { isPro, isEnterprise, setShowUpgradeModal, setUpgradeMessage } = useSubscription();
  const EXPORT_LIMIT = isEnterprise ? Infinity : 500;
  const { user, isSignedIn } = useUser();

  const [research, setResearch] = useState([]);
  const [detail, setDetail] = useState([]);
  const [dualInterest, setDualInterest] = useState([]);         // research + ownership
  const [paymentOwnershipDual, setPaymentOwnershipDual] = useState([]); // payments + ownership
  const [researchGeneralDual, setResearchGeneralDual] = useState([]);   // research + general
  const [studies, setStudies] = useState([]);
  const [products, setProducts] = useState([]);
  const [ownershipDetail, setOwnershipDetail] = useState([]);
  const [generalPayments, setGeneralPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [isWatched, setIsWatched] = useState(false);
  const [watchedCompanyNames, setWatchedCompanyNames] = useState(new Map());
  const [companyWatchLoading, setCompanyWatchLoading] = useState(new Set());
  const [screenPanelCompanyId, setScreenPanelCompanyId] = useState(null);
  const [exclusions, setExclusions] = useState({ oig: [], sam: [] });
  const [exclusionLoading, setExclusionLoading] = useState(true);
  const [directResearch, setDirectResearch] = useState([]);
  const [researchStates, setResearchStates] = useState([]);
  const [researchYear, setResearchYear] = useState(2025);
  const [selectedYear, setSelectedYear] = useState("all");

  useEffect(() => {
    if (!npi || npi === "unknown") { setLoading(false); return; }
    setLoading(true);
    Promise.all([
      getResearchByNPI(npi),
      getResearchDetailByNPI(npi),
      getResearchOwnershipDualInterest(npi),
      getResearchGeneralDualInterestByNPI(npi),
      getDualInterestByNPI(npi),
      getDirectResearchByNPI(npi),
    ])
      .then(([researchData, detailData, dualData, resGenData, payOwnershipData, directData]) => {
        setResearch(researchData || []);
        setDetail(detailData || []);
        setDualInterest(dualData || []);
        setResearchGeneralDual(resGenData || []);
        setPaymentOwnershipDual(payOwnershipData || []);
        setDirectResearch(directData || []);
        // Note: getDirectResearchByNPI was already imported and added in PhysicianOwnersPage session
        if (researchData?.length > 0) {
          getResearchStudiesByNPI(npi)
            .then((s) => setStudies(s || []))
            .catch(() => setStudies([]));
          getResearchProductsByNPI(npi)
            .then((p) => setProducts(p || []))
            .catch(() => setProducts([]));
        }
        if ((dualData || []).length > 0) {
          getOwnershipDetailByNPI(npi)
            .then((o) => setOwnershipDetail(o || []))
            .catch(() => setOwnershipDetail([]));
        }
        // Fetch general payments for general vs research chart
        if ((resGenData || []).length > 0) {
          const companyIds = (resGenData || []).map((g) => g.companyId).filter(Boolean);
          getGeneralPaymentsByNPIAndCompany(npi, companyIds)
            .then((gp) => setGeneralPayments(gp || []))
            .catch(() => setGeneralPayments([]));
        }
      })
      .catch(() => {
        setResearch([]);
        setDetail([]);
        setDualInterest([]);
        setResearchGeneralDual([]);
        setPaymentOwnershipDual([]);
        setDirectResearch([]);
      })
      .finally(() => setLoading(false));
  }, [npi]);

  // Fetch research state payments for the latest year with data
  useEffect(() => {
    if (!npi || npi === "unknown") return;
    const latestYear = detail.length > 0
      ? Math.max(...detail.map((d) => d.programYear))
      : 2025;
    setResearchYear(latestYear);
    getResearchStatePayments(npi, latestYear)
      .then(setResearchStates)
      .catch(() => setResearchStates([]));
  }, [npi, detail]);

useEffect(() => {
    if (!npi || npi === "unknown") { setExclusionLoading(false); return; }
    setExclusionLoading(true);
    const nameParts = name.split(" ");
    const first = nameParts[0] || "";
    const last = nameParts[nameParts.length - 1] || "";
    checkExclusions(npi, first, last, providerState)
      .then((data) => setExclusions({ oig: data.oig || [], sam: data.sam || [] }))
      .catch(() => setExclusions({ oig: [], sam: [] }))
      .finally(() => setExclusionLoading(false));
  }, [npi, name]);

  useEffect(() => {
    if (!isSignedIn || !user) return;
    getWatchedCompanies(user.id)
      .then((data) => {
        setWatchedCompanyNames(new Map(data.map((c) => [c.company_name, true])));
      })
      .catch(() => {});
  }, [isSignedIn, user]);

  async function handleCompanyWatchToggle(e, companyName, companyId) {
    e.preventDefault();
    e.stopPropagation();
    if (!isSignedIn) {
      setUpgradeMessage("Sign in to watch companies and receive email alerts.");
      setShowUpgradeModal(true);
      return;
    }
    if (!isPro) {
      setUpgradeMessage("Watching companies is a Pro feature. Upgrade to get notified when new payment data is published.");
      setShowUpgradeModal(true);
      return;
    }
    setCompanyWatchLoading((prev) => new Set(prev).add(companyName));
    try {
      if (watchedCompanyNames.get(companyName)) {
        await unwatchCompany(user.id, companyName);
        setWatchedCompanyNames((prev) => new Map(prev).set(companyName, false));
      } else {
        await watchCompany(user.id, companyName, companyId);
        await queueConfirmation(user.id, "company", companyName, companyName, { alert_payments: true });
        setWatchedCompanyNames((prev) => new Map(prev).set(companyName, true));
      }
    } catch (err) {
      console.error("Company watch toggle error:", err);
    } finally {
      setCompanyWatchLoading((prev) => { const s = new Set(prev); s.delete(companyName); return s; });
    }
  }

  // Derived values
  const totalResearch = research.reduce((s, r) => s + r.totalAmount, 0);
  const totalStudies = research.reduce((s, r) => s + r.studyCount, 0);
  const totalCompanies = research.length;

  // Available years for dropdown — derived from actual data
  const availableYears = [
    ...new Set([
      ...detail.map((d) => d.programYear),
      ...directResearch.map((d) => d.year),
    ])
  ].filter(Boolean).sort((a, b) => b - a); // descending

  // Filtered data based on selectedYear
  const isAllYears = selectedYear === "all";
  const filteredDetail = isAllYears ? detail : detail.filter((d) => d.programYear === selectedYear);
  const filteredDirectResearch = isAllYears ? directResearch : directResearch.filter((d) => d.year === selectedYear);
  const filteredStudies = isAllYears ? studies : studies.filter((s) => s.programYear === selectedYear);
  const filteredProducts = isAllYears
    ? products
    : (() => {
        // When a year is selected, derive products from studies for exact year match
        // studies has programYear + productName so we can filter precisely
        const productsInYear = new Set(
          filteredStudies
            .map((s) => s.productName)
            .filter(Boolean)
        );
        // Return matching product records from the full products array
        // Falls back to range overlap if no study-level product data exists
        const exact = products.filter((p) => productsInYear.has(p.productName));
        return exact.length > 0
          ? exact
          : products.filter((p) => p.firstYear <= selectedYear && p.latestYear >= selectedYear);
      })();

  // Merge direct-only companies into research list for display
  // Companies that paid the physician directly but have no PI-based research
  // Year-filtered research companies — filter by selectedYear using detail data
  const filteredResearch = isAllYears
    ? research
    : research.filter((r) =>
        detail.some((d) => d.companyId === r.companyId && d.programYear === selectedYear) ||
        directResearch.some((d) => d.companyId === r.companyId && d.year === selectedYear)
      );

  const piCompanyIds = new Set(filteredResearch.map((r) => r.companyId));
  const directOnlyCompanies = Object.values(
    filteredDirectResearch
      .filter((d) => !piCompanyIds.has(d.companyId))
      .reduce((acc, d) => {
        if (!acc[d.companyId]) {
          acc[d.companyId] = {
            companyId: d.companyId,
            companyName: d.companyName,
            totalAmount: 0,
            paymentCount: 0,
            studyCount: 0,
            firstYear: d.year,
            latestYear: d.year,
            hasActiveResearch: false,
            primaryContext: null,
            primaryProduct: d.productName || null,
            primaryProductType: d.productType || null,
            latestNctId: null,
            isDirectOnly: true,
          };
        }
        acc[d.companyId].totalAmount += d.totalAmount;
        acc[d.companyId].paymentCount += d.paymentCount;
        acc[d.companyId].firstYear = Math.min(acc[d.companyId].firstYear, d.year);
        acc[d.companyId].latestYear = Math.max(acc[d.companyId].latestYear, d.year);
        return acc;
      }, {})
  );
  const mergedResearch = [
    ...filteredResearch,
    ...directOnlyCompanies,
  ].sort((a, b) => b.totalAmount - a.totalAmount);

  // Direct research payments — physician as covered recipient (paid personally)
  const totalDirectResearch = directResearch.reduce((s, r) => s + r.totalAmount, 0);
  const totalDirectPaymentCount = directResearch.reduce((s, r) => s + r.paymentCount, 0);
  const hasDirectResearch = totalDirectResearch > 0;
  const activeResearch = research.filter((r) => r.hasActiveResearch);
  const hasDualInterest = dualInterest.length > 0;         // research + ownership
  const hasDualInterestSamePeriod = dualInterest.some((d) => d.hasDualInterestSamePeriod);
  const hasResearchGeneralDual = researchGeneralDual.length > 0;
  const hasPaymentOwnershipDual = paymentOwnershipDual.length > 0;
  const tripleInterestCompanyIds = new Set(
    paymentOwnershipDual
      .filter((d) => d.hasTripleInterestAnyYear)
      .map((d) => d.companyId)
  );
  const hasTripleInterest = tripleInterestCompanyIds.size > 0;
  const proGate = !isPro;

  if (loading) {
    return (
      <main className="max-w-3xl mx-auto px-4 py-10">
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="card animate-pulse">
              <div className="h-4 bg-white/10 rounded w-2/3 mb-3" />
              <div className="h-3 bg-white/5 rounded w-1/3" />
            </div>
          ))}
        </div>
      </main>
    );
  }

  return (
    <main className="max-w-3xl mx-auto px-4 py-10">
      {/* ── Print styles ──────────────────────────────────────────────────── */}
      <style>{`
        @media print {
          @page { margin: 0.75in; size: letter; }
          body {
            background: white !important;
            color: #111 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          /* Hide everything not meant for print */
          nav, header, footer,
          .print-hide { display: none !important; }

          /* Force all divs with inline background-color to print */
          div[style] { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          div[style*="background-color: rgba(251,191,36"] { background-color: #d97706 !important; }
          div[style*="background-color: rgba(139,92,246"] { background-color: #7c3aed !important; }
          div[style*="background-color: rgba(34,197,94"]  { background-color: #16a34a !important; }
          div[style*="background-color: rgba(59,130,246"] { background-color: #2563eb !important; }
          div[style*="background-color: rgba(239,68,68"]  { background-color: #dc2626 !important; }
          div[style*="background-color: rgba(249,115,22"] { background-color: #ea580c !important; }
          div[style*="background-color: rgba(20,184,166"] { background-color: #0f766e !important; }
          div[style*="background-color: rgba(236,72,153"] { background-color: #db2777 !important; }
          div[style*="background-color: rgba(255,255,255,0.04"] { display: none !important; }
          div[style*="background-color: rgba(255,255,255,0.07"] { display: none !important; }
          main { background: white !important; max-width: 100% !important; padding: 0 !important; }
          .card { background: white !important; border: 1px solid #e5e7eb !important; border-radius: 8px !important; box-shadow: none !important; break-inside: avoid; }
          .text-white { color: #111 !important; }
          .text-white\\/80, .text-white\\/70, .text-white\\/60, .text-white\\/50 { color: #374151 !important; }
          .text-white\\/40, .text-white\\/30, .text-white\\/25, .text-white\\/20 { color: #6b7280 !important; }
          .text-teal-400 { color: #0f766e !important; }
          .text-amber-400 { color: #b45309 !important; }
          .text-red-400 { color: #dc2626 !important; }
          .text-orange-400 { color: #ea580c !important; }
          .text-violet-400 { color: #7c3aed !important; }
          .text-green-400 { color: #16a34a !important; }
          .text-blue-400 { color: #2563eb !important; }
          .bg-red-500\\/10 { background: #fef2f2 !important; border-color: #fca5a5 !important; }
          .bg-amber-500\\/10 { background: #fffbeb !important; border-color: #fcd34d !important; }
          .bg-teal-500\\/10 { background: #f0fdfa !important; border-color: #5eead4 !important; }
          .bg-blue-500\\/10 { background: #eff6ff !important; border-color: #93c5fd !important; }
          .bg-orange-500\\/10 { background: #fff7ed !important; border-color: #fdba74 !important; }
          svg text { fill: #374151 !important; }

          /* Force chart bars to full opacity on white background */
          [style*="rgba(251,191,36"] { background-color: #d97706 !important; opacity: 1 !important; }
          [style*="rgba(139,92,246"] { background-color: #7c3aed !important; opacity: 1 !important; }
          [style*="rgba(34,197,94"]  { background-color: #16a34a !important; opacity: 1 !important; }
          [style*="rgba(59,130,246"] { background-color: #2563eb !important; opacity: 1 !important; }
          [style*="rgba(239,68,68"]  { background-color: #dc2626 !important; opacity: 1 !important; }
          [style*="rgba(249,115,22"] { background-color: #ea580c !important; opacity: 1 !important; }
          [style*="rgba(20,184,166"] { background-color: #0f766e !important; opacity: 1 !important; }
          [style*="rgba(236,72,153"] { background-color: #db2777 !important; opacity: 1 !important; }
          [style*="rgba(255,255,255,0.04"] { display: none !important; }

          /* Hide chart interactive controls */
          select { display: none !important; }

          /* Hide Export to CSV buttons inside chart cards */
          .card button { display: none !important; }

          /* Hide footnote rows inside chart cards */
          .card .flex.items-center.justify-between.flex-wrap { display: none !important; }
          .card .mt-3.flex.items-center { display: none !important; }

          /* Hide "show company breakdown" toggle and footnote in PreclinicalSplit */
          .card .border-t.border-white\\/5 button { display: none !important; }
          .card .border-t.border-white\\/5 p { display: none !important; }

          /* Hide the header row controls (dropdown + toggle) inside chart cards */
          .card .flex.items-center.justify-between.mb-4 > div:last-child { display: none !important; }
          .card .flex.items-center.gap-2:last-child { display: none !important; }
        }
      `}</style>

      {/* ── Print-only report header ───────────────────────────────────────── */}
      <div className="hidden print:block mb-4 pb-3 border-b border-gray-200">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs text-gray-400 uppercase tracking-widest mb-1">ProviderPayWatch · Research Payment Report</p>
            <h1 className="text-2xl font-bold text-gray-900">{name}</h1>
            <p className="text-sm text-gray-500 mt-1">
              NPI: {npi}
              {specialty ? ` · ${specialty}` : ""}
              {providerState ? ` · ${providerState}` : ""}
            </p>
            {researchStates.length > 0 && providerState && (() => {
              const outOfState = researchStates.filter((s) => s.state !== providerState);
              if (outOfState.length === 0) return null;
              return (
                <div className="flex items-center gap-1.5 flex-wrap mt-2">
                  <span style={{ fontSize: "11px", color: "#9ca3af" }}>{researchYear} research in:</span>
                  {researchStates.map((s) => {
                    const isHome = s.state === providerState;
                    const amt = s.totalPayments >= 1_000_000
                      ? `$${(s.totalPayments / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`
                      : s.totalPayments >= 1_000
                      ? `$${(s.totalPayments / 1_000).toFixed(0)}K`
                      : `$${s.totalPayments.toFixed(0)}`;
                    return (
                      <span key={s.state} style={{
                        fontSize: "11px", padding: "2px 8px", borderRadius: "9999px",
                        border: `1px solid ${isHome ? "#d1d5db" : "#0d9488"}`,
                        color: isHome ? "#9ca3af" : "#0f766e",
                        background: isHome ? "#f9fafb" : "#f0fdfa",
                      }}>
                        {s.state} {amt}
                      </span>
                    );
                  })}
                </div>
              );
            })()}
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-400">Generated {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</p>
            <p className="text-xs text-gray-400 mt-0.5">providerpaywatch.com</p>
          </div>
        </div>
      </div>

      {/* Back link */}
      <Link
        to={`/physician/${npi}?name=${encodeURIComponent(name)}&state=${providerState}`}
        className="print:hidden text-white/40 hover:text-white/70 text-sm transition-colors"
      >
        ← Back to provider profile
      </Link>

      {/* Header */}
      <div className="mt-4 mb-6">
        <div className="print:hidden flex items-center gap-2 mb-1">
          <span className="text-teal-400/60 text-xs uppercase tracking-widest">Research Payments</span>
        </div>
        <h1 className="print:hidden font-display text-4xl text-white leading-tight">{name}</h1>
        <div className="print:hidden flex items-center gap-3 mt-1 flex-wrap">
          {npi && npi !== "unknown" && (
            <p className="text-white/30 text-xs font-mono">NPI: {npi}</p>
          )}
          {specialty && <p className="text-white/30 text-xs">{specialty}</p>}
          {providerState && <p className="text-white/30 text-xs">{providerState}</p>}
          {/* Out-of-state research payment badges */}
          {researchStates.length > 0 && providerState && (() => {
            const outOfState = researchStates.filter((s) => s.state !== providerState);
            if (outOfState.length === 0) return null;
            return (
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-white/20 text-xs shrink-0">{researchYear} research in:</span>
                {researchStates.map((s) => {
                  const isHome = s.state === providerState;
                  return (
                    <span
                      key={s.state}
                      className={`text-xs px-2 py-0.5 rounded-full border ${
                        isHome
                          ? "bg-white/5 border-white/10 text-white/30"
                          : "bg-teal-500/10 border-teal-500/25 text-teal-400/80"
                      }`}
                    >
                      {s.state} {s.totalPayments >= 1_000_000
                        ? `$${(s.totalPayments / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`
                        : s.totalPayments >= 1_000
                        ? `$${(s.totalPayments / 1_000).toFixed(0)}K`
                        : `$${s.totalPayments.toFixed(0)}`}
                    </span>
                  );
                })}
              </div>
            );
          })()}
          <button
            onClick={() => {
              navigator.clipboard.writeText(window.location.href);
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            }}
            className={`print:hidden flex items-center gap-1.5 text-xs px-3 py-1 rounded-lg border transition-all ${
              copied
                ? "text-green-400 border-green-500/30 bg-green-500/10"
                : "text-white/60 border-white/20 hover:text-white hover:border-white/40 bg-white/5"
            }`}
          >
            {copied ? <><span>✓</span><span>Link copied!</span></> : <><span>⎘</span><span>Share</span></>}
          </button>
          <div className="print:hidden">
            <WatchProviderButton
              npi={npi}
              providerName={name}
              initialWatched={isWatched}
              onWatchChange={(n, watched) => setIsWatched(watched)}
            />
          </div>
          <div className="print:hidden">
            <CompareButton provider={{
              npi,
              firstName: name.split(" ")[0] || "",
              lastName: name.split(" ").slice(1).join(" ") || "",
              state: providerState,
              totalPayments: totalResearch,
            }} />
          </div>
          {isPro ? (
            <button
              onClick={() => window.print()}
              className="print:hidden flex items-center gap-1.5 text-xs px-3 py-1 rounded-lg border border-white/20 text-white/60 hover:text-white hover:border-white/40 bg-white/5 transition-all"
            >
              <span>⎙</span><span>Print / Save PDF</span>
            </button>
          ) : (
            <button
              onClick={() => { setUpgradeMessage("Upgrade to Pro to print and save research profiles as PDF."); setShowUpgradeModal(true); }}
              className="print:hidden flex items-center gap-1.5 text-xs px-3 py-1 rounded-lg border border-white/20 text-white/40 bg-white/5 transition-all"
            >
              <span>🔒</span><span>Print / Save PDF</span>
            </button>
          )}
        </div>

        {/* No data */}
        {research.length === 0 && (
          <div className="card text-center py-12 mt-6">
            <p className="text-white/50 text-sm">No research payment records found for this provider.</p>
            <p className="text-white/25 text-xs mt-2">CMS Open Payments Research data covers 2016–2025.</p>
          </div>
        )}

        {/* ── Exclusion Banners ─────────────────────────────────────────── */}
        {!exclusionLoading && (exclusions.oig.length > 0 || exclusions.sam.length > 0) && (
          <div className="mt-4 space-y-2">
            {exclusions.oig.map((e, i) => (
              <div key={i} className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-4">
                  <div className="flex items-start gap-2">
                    <span className="text-red-400 text-sm mt-0.5">⚠</span>
                    <div>
                      <p className="text-red-400 font-medium text-sm">OIG Excluded</p>
                      {e.matchType === "name_state" && (
                        <p className="text-yellow-400/80 text-xs mt-0.5">Possible name and state match — verify manually</p>
                      )}
                    </div>
                  </div>
                  <div className="sm:text-right ml-6 sm:ml-0">
                    <p className="text-red-300/70 text-xs">{e.exclusionDate}</p>
                    <p className="text-red-300/50 text-xs mt-0.5">{e.exclusionType}</p>
                  </div>
                </div>
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-red-500/20">
                  {e.reinstatedDate && <p className="text-green-400 text-xs">Reinstated: {e.reinstatedDate}</p>}
                  <a href="https://exclusions.oig.hhs.gov" target="_blank" rel="noopener noreferrer"
                    className="text-red-400/50 hover:text-red-400 text-xs transition-colors ml-auto">
                    Verify on OIG website →
                  </a>
                </div>
              </div>
            ))}
            {exclusions.sam.map((e, i) => (
              <div key={i} className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-4">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-4">
                  <div className="flex items-start gap-2">
                    <span className="text-orange-400 text-sm mt-0.5">⚠</span>
                    <div>
                      <p className="text-orange-400 font-medium text-sm">SAM.gov Excluded</p>
                      {e.matchType === "name_state" && (
                        <p className="text-yellow-400/80 text-xs mt-0.5">Possible name and state match — verify manually</p>
                      )}
                    </div>
                  </div>
                  <div className="sm:text-right ml-6 sm:ml-0">
                    <p className="text-orange-300/70 text-xs">{e.exclusionDate}</p>
                    <p className="text-orange-300/50 text-xs mt-0.5">{e.exclusionType}</p>
                    {e.excludingAgency && <p className="text-orange-300/40 text-xs mt-0.5">{e.excludingAgency}</p>}
                  </div>
                </div>
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-orange-500/20">
                  {e.terminationDate && e.terminationDate !== "Indefinite" && (
                    <p className="text-orange-300/50 text-xs">Terminates: {e.terminationDate}</p>
                  )}
                  <a href="https://sam.gov/search/?index=ei&page=1&pageSize=25&sort=-score&sfm%5Bstatus%5D%5B0%5D=Active"
                    target="_blank" rel="noopener noreferrer"
                    className="text-orange-400/50 hover:text-orange-400 text-xs transition-colors ml-auto">
                    Verify on SAM.gov →
                  </a>
                </div>
              </div>
            ))}
            <p className="text-white/20 text-xs">
              Sources: HHS OIG LEIE (updated monthly) · SAM.gov (updated daily) · Some records may contain data from Dun &amp; Bradstreet.
            </p>
          </div>
        )}

        {/* ── Conflict Banners ──────────────────────────────────────────── */}
        {(hasTripleInterest || hasDualInterest || hasResearchGeneralDual) && (
          <div className="mt-4 space-y-3">
            <ResearchConflictBanners
              hasTripleInterest={hasTripleInterest}
              tripleInterestCompanyIds={tripleInterestCompanyIds}
              hasDualInterest={hasDualInterest}
              hasDualInterestSamePeriod={hasDualInterestSamePeriod}
              dualInterest={dualInterest}
              hasPaymentOwnershipDual={hasPaymentOwnershipDual}
              paymentOwnershipDual={paymentOwnershipDual}
              researchGeneralDual={researchGeneralDual}
              hasResearchGeneralDual={hasResearchGeneralDual}
              isPro={isPro}
              setUpgradeMessage={setUpgradeMessage}
              setShowUpgradeModal={setShowUpgradeModal}
            />
          </div>
        )}

        {/* ── Page tab bar ──────────────────────────────────────────────── */}
        <div className="print:hidden mt-4">
          <ProviderPageTabs
            npi={npi}
            name={name}
            state={providerState}
            specialty={specialty}
            totalPayments={totalPayments}
            ownershipCount={paymentOwnershipDual.length}
            activeOwnershipCount={0}
            researchCount={research.length}
            researchPayments={research.reduce((s, r) => s + (r.paymentCount || 0), 0)}
            researchYears={detail.length > 0 ? [...new Set(detail.map((d) => d.programYear))].length : 0}
            activePage="research"
          />
        </div>

        {/* Stats Row */}
        {research.length > 0 && (() => {
          const latestYear = isAllYears
            ? (detail.length > 0 ? Math.max(...detail.map((d) => d.programYear)) : null)
            : selectedYear;
          const latestYearAssociated = latestYear
            ? detail.filter((d) => d.programYear === latestYear).reduce((s, d) => s + d.totalAmount, 0)
            : 0;
          const latestYearDirect = latestYear
            ? directResearch.filter((d) => d.year === latestYear).reduce((s, d) => s + d.totalAmount, 0)
            : 0;
          const latestYearDirectCount = latestYear
            ? directResearch.filter((d) => d.year === latestYear).reduce((s, d) => s + d.paymentCount, 0)
            : 0;

          return (
            <div className="flex gap-3 mt-4 flex-wrap">
              {/* 1 — All years associated research funding */}
              <div className="card py-3 px-4 text-center">
                <div className="text-teal-400 font-display text-2xl">
                  {formatCurrency(totalResearch)}
                </div>
                <div className="text-white/30 text-xs mt-0.5">associated research funding</div>
                <div className="text-white/20 text-xs mt-0.5">all years · as principal investigator</div>
              </div>

              {/* 2 — Latest year associated research funding */}
              {latestYear && latestYearAssociated > 0 && (
                <div className="card py-3 px-4 text-center">
                  <div className="text-teal-400 font-display text-2xl">
                    {formatCurrency(latestYearAssociated)}
                  </div>
                  <div className="text-white/30 text-xs mt-0.5">associated research funding</div>
                  <div className="text-white/20 text-xs mt-0.5">{isAllYears ? `latest year (${latestYear})` : `${latestYear}`}</div>
                </div>
              )}

              {/* 3 — All years direct research payments */}
              {hasDirectResearch && (
                <div className="card py-3 px-4 text-center">
                  <div className="text-teal-400 font-display text-2xl">
                    {formatCurrency(totalDirectResearch)}
                  </div>
                  <div className="text-white/30 text-xs mt-0.5">direct research payments</div>
                  <div className="text-white/20 text-xs mt-0.5">all years · {totalDirectPaymentCount} {totalDirectPaymentCount === 1 ? "payment" : "payments"}</div>
                </div>
              )}

              {/* 4 — Latest year direct research payments */}
              {hasDirectResearch && latestYear && latestYearDirect > 0 && (
                <div className="card py-3 px-4 text-center">
                  <div className="text-teal-400 font-display text-2xl">
                    {formatCurrency(latestYearDirect)}
                  </div>
                  <div className="text-white/30 text-xs mt-0.5">direct research payments</div>
                  <div className="text-white/20 text-xs mt-0.5">{isAllYears ? `latest year (${latestYear})` : `${latestYear}`} · {latestYearDirectCount} {latestYearDirectCount === 1 ? "payment" : "payments"}</div>
                </div>
              )}

              {/* Remaining stat cards */}
              <div className="card py-3 px-4 text-center">
                <div className="text-white font-display text-2xl">{totalCompanies}</div>
                <div className="text-white/30 text-xs mt-0.5">
                  {totalCompanies === 1 ? "company" : "companies"}
                </div>
              </div>
              <div className="card py-3 px-4 text-center">
                <div className="text-white font-display text-2xl">{totalStudies}</div>
                <div className="text-white/30 text-xs mt-0.5">
                  {totalStudies === 1 ? "study" : "studies"}
                </div>
              </div>
              {activeResearch.length > 0 && (
                <div className="card py-3 px-4 text-center">
                  <div className="text-green-400 font-display text-2xl">{activeResearch.length}</div>
                  <div className="text-white/30 text-xs mt-0.5">active (2025)</div>
                </div>
              )}
            </div>
          );
        })()}
      </div>

      {/* Year filter dropdown — affects charts below, not all-years stats */}
      {research.length > 0 && availableYears.length > 1 && (
        <div className="print:hidden flex items-center gap-3 mt-4 mb-2">
          <span className="text-white/30 text-xs uppercase tracking-widest">Filter by year</span>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value === "all" ? "all" : parseInt(e.target.value))}
            className="bg-navy-700 border border-white/10 text-white rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:border-teal-500/60"
          >
            <option value="all">All years</option>
            {availableYears.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          {!isAllYears && (
            <button
              onClick={() => setSelectedYear("all")}
              className="text-white/30 hover:text-white/60 text-xs transition-colors"
            >
              Clear
            </button>
          )}
        </div>
      )}

      {/* Charts — Pro gated */}
      {research.length > 0 && (
        <div className={`relative ${proGate ? "overflow-hidden rounded-2xl" : ""}`}>
          {proGate && (
            <div className="print:hidden absolute inset-0 backdrop-blur-md bg-navy-900/60 z-10 rounded-2xl flex flex-col items-center justify-center gap-3 p-6">
              <p className="text-white/70 font-medium text-sm text-center">
                🔒 Research charts are a Pro feature
              </p>
              <p className="text-white/40 text-xs text-center max-w-xs">
                Upgrade to Pro to see year-by-year research funding trends, context breakdowns, and expenditure analysis.
              </p>
              <button
                onClick={() => {
                  setUpgradeMessage("Upgrade to Pro to access full research payment history charts and analysis.");
                  setShowUpgradeModal(true);
                }}
                className="btn-primary text-xs px-5 py-2 mt-1"
              >
                Upgrade to Pro
              </button>
            </div>
          )}

          {/* These three charts are in the print spec */}
          <div className="print-chart-wrapper">
            <ResearchPaymentChart detail={detail} name={name} npi={npi} directResearch={directResearch} totalCompanyCount={mergedResearch.length} />
          </div>
          <FundingComparisonCard
            totalResearch={filteredDetail.reduce((s, d) => s + d.totalAmount, 0)}
            totalDirectResearch={filteredDirectResearch.reduce((s, d) => s + d.totalAmount, 0)}
            hasDirectResearch={filteredDirectResearch.length > 0}
            selectedYear={selectedYear}
          />
          <PreclinicalSplit research={research} studies={filteredStudies} dualInterest={dualInterest} directResearch={filteredDirectResearch} name={name} npi={npi} />
          <DirectPaymentBreakdownCard directResearch={filteredDirectResearch} />
          <ProductExposureChart products={filteredProducts} name={name} npi={npi} />

          {/* These are screen only */}
          <div className="print:hidden">
            <ExpenditureChart detail={detail} />
            {hasDualInterest && ownershipDetail.length > 0 && (
              <OwnershipVsResearchChart
                conflicts={dualInterest}
                ownershipDetail={ownershipDetail}
                researchDetail={detail}
                directResearch={directResearch}
                providerName={name}
                npi={npi}
              />
            )}
            {hasResearchGeneralDual && generalPayments.length > 0 && (
              <GeneralVsResearchChart
                conflicts={researchGeneralDual}
                generalPayments={generalPayments}
                researchDetail={detail}
                directResearch={directResearch}
                providerName={name}
                npi={npi}
              />
            )}
          </div>
        </div>
      )}

      {/* Research Company List */}
      {research.length > 0 && (
        <>
          {/* Print-only top-5 research companies */}
          <div className="hidden print:block mt-6 mb-4">
            <h2 className="text-gray-500 text-xs uppercase tracking-widest mb-3">Top Research Companies (Top 5)</h2>
            <div className="space-y-2">
              {research.slice(0, 5).map((r, i) => {
                const isTriple = tripleInterestCompanyIds.has(r.companyId);
                const isDual = !isTriple && (
                  dualInterest.some((d) => d.companyId === r.companyId) ||
                  researchGeneralDual.some((g) => g.companyId === r.companyId) ||
                  paymentOwnershipDual.some((p) => p.companyId === r.companyId)
                );
                return (
                  <div key={i} className={`border rounded-lg px-4 py-3 ${r.hasActiveResearch ? "border-teal-200 bg-teal-50" : "border-gray-200"}`}>
                    <div className="flex justify-between items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-gray-900 text-sm font-medium">{r.companyName}</p>
                          {r.hasActiveResearch && <span className="text-xs text-green-700 border border-green-200 bg-green-50 px-1.5 py-0.5 rounded-full">Active 2025</span>}
                          {isTriple && <span className="text-xs text-red-600 border border-red-200 bg-red-50 px-1.5 py-0.5 rounded-full">⚠ Triple Interest</span>}
                          {isDual && <span className="text-xs text-orange-600 border border-orange-200 bg-orange-50 px-1.5 py-0.5 rounded-full">⚑ Dual Interest</span>}
                        </div>
                        <p className="text-gray-500 text-xs mt-0.5">
                          {r.firstYear === r.latestYear ? `Reported ${r.firstYear}` : `${r.firstYear}–${r.latestYear}`}
                          {" · "}{r.studyCount} {r.studyCount === 1 ? "study" : "studies"}
                          {" · "}{r.paymentCount} {r.paymentCount === 1 ? "payment" : "payments"}
                        </p>
                        {r.primaryContext && <p className="text-gray-400 text-xs mt-0.5">{r.primaryContext}</p>}
                        {r.primaryProduct && <p className="text-gray-400 text-xs mt-0.5 italic">{r.primaryProduct}</p>}
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-teal-700 text-sm font-mono font-medium">{formatCurrency(r.totalAmount)}</p>
                        <p className="text-gray-400 text-xs">total funding</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            {research.length > 5 && (
              <p className="text-gray-400 text-xs mt-2">Showing 5 of {research.length} companies · export full list from ProviderPayWatch</p>
            )}
          </div>

          <div className="print:hidden flex items-center justify-between mb-3 mt-2">
            <h2 className="text-white/40 text-xs uppercase tracking-widest">
              Research companies
            </h2>
            <div className="flex items-center gap-3">
              <span className="text-white/25 text-xs">2016–2025</span>
              {isPro && (
                <button
                  onClick={() => exportResearchCompaniesCsv(mergedResearch, name, npi, tripleInterestCompanyIds, dualInterest, researchGeneralDual, paymentOwnershipDual, EXPORT_LIMIT, filteredDirectResearch)}
                  className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-white/10 text-white/40 hover:text-white/70 hover:border-white/20 transition-all"
                >
                  ⬇ Export all to CSV
                </button>
              )}
            </div>
          </div>

          <div className={`print:hidden relative space-y-3 ${proGate ? "overflow-hidden rounded-2xl" : ""}`}>
            {proGate && (
              <div className="absolute inset-0 backdrop-blur-md bg-navy-900/60 z-10 rounded-2xl flex flex-col items-center justify-center gap-3 p-6">
                <p className="text-white/70 font-medium text-sm text-center">
                  🔒 Full research records are a Pro feature
                </p>
                <p className="text-white/40 text-xs text-center max-w-xs">
                  Upgrade to Pro to see company names, funding amounts, study counts, and conflict flags.
                </p>
                <button
                  onClick={() => {
                    setUpgradeMessage("Upgrade to Pro to access full research payment records.");
                    setShowUpgradeModal(true);
                  }}
                  className="btn-primary text-xs px-5 py-2 mt-1"
                >
                  Upgrade to Pro
                </button>
              </div>
            )}

            {(proGate ? mergedResearch.slice(0, 3) : mergedResearch.slice(0, 10)).map((r, i) => {
              const isTriple = tripleInterestCompanyIds.has(r.companyId);
              const isDual = !isTriple && (
                dualInterest.some((d) => d.companyId === r.companyId) ||
                researchGeneralDual.some((g) => g.companyId === r.companyId) ||
                paymentOwnershipDual.some((p) => p.companyId === r.companyId)
              );
              // Direct research payments from this company — respects year filter
              const directRows = filteredDirectResearch.filter((d) => d.companyId === r.companyId);
              const directTotal = directRows.reduce((s, d) => s + d.totalAmount, 0);
              const directCount = directRows.reduce((s, d) => s + d.paymentCount, 0);
              const hasDirectFromThisCompany = directTotal > 0;
              // Year-aware amount display:
              // When a year is selected, show that year's amount from detail
              // When all years, show latest year amount then total below
              const displayYear = isAllYears ? r.latestYear : selectedYear;
              const latestYear = displayYear;
              const latestYearAmount = displayYear
                ? detail
                    .filter((d) => d.companyId === r.companyId && d.programYear === displayYear)
                    .reduce((s, d) => s + d.totalAmount, 0)
                : 0;
              return (
                <div
                  key={i}
                  className={`rounded-xl border px-4 py-4 ${
                    r.hasActiveResearch
                      ? "bg-teal-500/5 border-teal-500/20"
                      : "bg-white/[0.03] border-white/[0.08]"
                  }`}
                >
                  <div className="flex justify-between items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className={`text-sm font-medium truncate ${proGate ? "blur-sm select-none" : "text-white/80"}`}>
                          {proGate ? "Company Name Hidden" : r.companyName}
                        </p>
                        {r.hasActiveResearch && (
                          <span className="bg-green-500/20 border border-green-500/30 text-green-400 text-xs px-1.5 py-0.5 rounded-full shrink-0">
                            Active 2025
                          </span>
                        )}
                        {isTriple ? (
                          <span className="bg-red-500/10 border border-red-500/30 text-red-400 text-xs px-1.5 py-0.5 rounded-full shrink-0">
                            ⚠ Triple Interest
                          </span>
                        ) : isDual ? (
                          <span className="bg-orange-500/10 border border-orange-500/30 text-orange-400 text-xs px-1.5 py-0.5 rounded-full shrink-0">
                            ⚑ Dual Interest
                          </span>
                        ) : null}
                      </div>
                      <p className="text-white/30 text-xs mt-0.5">
                        {r.firstYear === r.latestYear
                          ? `Reported ${r.firstYear}`
                          : `${r.firstYear}–${r.latestYear}`}
                        {r.isDirectOnly ? (
                          <span> · direct payments only</span>
                        ) : (
                          <>
                            {" · "}{r.studyCount} {r.studyCount === 1 ? "study" : "studies"}
                            {" · "}{r.paymentCount} {r.paymentCount === 1 ? "payment" : "payments"}
                          </>
                        )}
                      </p>
                      {r.primaryContext && (
                        <p className="text-white/40 text-xs mt-1">{r.primaryContext}</p>
                      )}
                      {r.primaryProduct && (
                        <p className="text-white/30 text-xs mt-0.5 italic">
                          {r.primaryProductType ? `${r.primaryProductType}: ` : ""}{r.primaryProduct}
                        </p>
                      )}
                      {r.latestNctId && !proGate && (
                        
                          <a href={`https://clinicaltrials.gov/study/${r.latestNctId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-teal-400/60 hover:text-teal-400 text-xs mt-1 transition-colors inline-block"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {r.latestNctId} → ClinicalTrials.gov
                        </a>
                      )}
                      {hasDirectFromThisCompany && !proGate && (
                        <p className="text-amber-400/70 text-xs mt-1.5 flex items-center gap-1">
                          <span>💳</span>
                          <span>
                            Also paid <span className="font-mono">{formatCurrency(directTotal)}</span> directly to provider
                            {" · "}{directCount} {directCount === 1 ? "payment" : "payments"}
                          </span>
                        </p>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      {latestYearAmount > 0 && !r.isDirectOnly ? (
                        <>
                          <p className={`text-sm font-mono font-medium ${proGate ? "blur-sm select-none" : "text-teal-400"}`}>
                            {formatCurrency(latestYearAmount)}
                          </p>
                          <p className="text-white/30 text-xs">{latestYear} funding</p>
                          {isAllYears && (
                            <>
                              <p className={`text-xs font-mono mt-1 ${proGate ? "blur-sm select-none" : "text-teal-400/60"}`}>
                                {formatCurrency(r.totalAmount)}
                              </p>
                              <p className="text-white/20 text-xs">total all years</p>
                            </>
                          )}
                        </>
                      ) : (
                        <>
                          <p className={`text-sm font-mono font-medium ${proGate ? "blur-sm select-none" : "text-teal-400"}`}>
                            {formatCurrency(r.totalAmount)}
                          </p>
                          <p className="text-white/30 text-xs">
                            {r.isDirectOnly ? "direct payments" : "total funding"}
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                  {isPro && !proGate && (
                    <div className="mt-3 flex justify-end gap-2">
                      {isEnterprise ? (
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setScreenPanelCompanyId(screenPanelCompanyId === r.companyId ? null : r.companyId);
                          }}
                          className="flex items-center gap-1.5 text-xs px-3 py-1 rounded-lg border border-red-500/30 text-red-400 hover:bg-red-500/10 transition-all"
                        >
                          🛡 Screen
                        </button>
                      ) : (
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setUpgradeMessage("Screening a company for conflicts against this provider is an Enterprise feature.");
                            setShowUpgradeModal(true);
                          }}
                          className="flex items-center gap-1.5 text-xs px-3 py-1 rounded-lg border border-red-500/30 bg-red-500/5 text-red-400/70 hover:bg-red-500/10 hover:text-red-400 transition-all"
                        >
                          🔒 Screen <span className="text-[9px] px-1 py-0.5 rounded-full bg-red-500/20 border border-red-500/30 text-red-300 uppercase tracking-wide">Enterprise</span>
                        </button>
                      )}
                      <button
                        onClick={(e) => handleCompanyWatchToggle(e, r.companyName, r.companyId)}
                        disabled={companyWatchLoading.has(r.companyName)}
                        className={`text-xs px-3 py-1 rounded-lg border transition-all ${
                          watchedCompanyNames.get(r.companyName)
                            ? "bg-gold-500/20 border-gold-500/40 text-gold-300"
                            : "border-white/20 text-white/40 hover:border-gold-500/40 hover:text-gold-300"
                        }`}
                      >
                        {companyWatchLoading.has(r.companyName)
                          ? "..."
                          : watchedCompanyNames.get(r.companyName)
                          ? "🔔 Watching"
                          : "🔕 Watch"}
                      </button>
                    </div>
                  )}
                  {screenPanelCompanyId === r.companyId && (
                    <div onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
                      <SingleProviderScreenPanel
                        companyId={r.companyId}
                        companyName={r.companyName}
                        npi={npi}
                        providerName={name}
                        userId={user?.id}
                        fetchFn={apiFetch}
                        onCancel={() => setScreenPanelCompanyId(null)}
                      />
                    </div>
                  )}
                </div>
              );
            })}

            {proGate && mergedResearch.length > 3 && (
              <div className="text-center text-white/30 text-xs pt-2 pb-4">
                +{mergedResearch.length - 3} more {mergedResearch.length - 3 === 1 ? "company" : "companies"} hidden
              </div>
            )}
          </div>

          {/* Bottom export row — screen only */}
          {isPro && (
            <div className="print:hidden mt-3 flex items-center justify-between flex-wrap gap-2">
              <span className="text-white/25 text-xs">
                {mergedResearch.length > 10
                  ? `Showing 10 of ${mergedResearch.length} companies · export for full list`
                  : `${mergedResearch.length} ${mergedResearch.length === 1 ? "company" : "companies"}`}
              </span>
              <button
                onClick={() => exportResearchCompaniesCsv(research, name, npi, tripleInterestCompanyIds, dualInterest, researchGeneralDual, paymentOwnershipDual, EXPORT_LIMIT)}
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-white/10 text-white/40 hover:text-white/70 hover:border-white/20 transition-all"
              >
                ⬇ Export all to CSV
              </button>
            </div>
          )}
        </>
      )}

      {/* Individual Studies List — screen only */}
      {isPro && filteredStudies.length > 0 && (
        <div className="print:hidden mt-8">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-white/40 text-xs uppercase tracking-widest">
              Individual studies{!isAllYears ? ` · ${selectedYear}` : ""}
            </h2>
            <span className="text-white/25 text-xs">
              {filteredStudies.length > 10
                ? `${Math.min(10, filteredStudies.length)} of ${filteredStudies.length} records`
                : `${filteredStudies.length} records`}
            </span>
          </div>

          <>
            <div className="space-y-2">
              {filteredStudies.slice(0, 10).map((s, i) => (
                  <div key={i} className="rounded-xl border border-white/[0.08] bg-white/[0.02] px-4 py-3">
                    <div className="flex justify-between items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-white/70 text-sm font-medium truncate">
                          {s.nameOfStudy || "Unnamed study"}
                        </p>
                        <p className="text-white/30 text-xs mt-0.5">
                          {s.companyName} · {s.programYear}
                          {s.contextOfResearch ? ` · ${s.contextOfResearch}` : ""}
                          {s.preclinicalIndicator === "Y" ? " · Preclinical" : ""}
                        </p>
                        {s.productName && (
                          <p className="text-white/25 text-xs mt-0.5 italic">
                            {s.productType ? `${s.productType}: ` : ""}{s.productName}
                          </p>
                        )}
                        {s.expenditureCategory1 && (
                          <p className="text-white/20 text-xs mt-0.5">{s.expenditureCategory1}</p>
                        )}
                        {s.nctId && (
                          
                            <a href={`https://clinicaltrials.gov/study/${s.nctId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-teal-400/60 hover:text-teal-400 text-xs mt-1 transition-colors inline-block"
                          >
                            {s.nctId} → ClinicalTrials.gov
                          </a>
                        )}
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-teal-400 text-sm font-mono font-medium">
                          {formatCurrency(s.totalAmount)}
                        </p>
                        <p className="text-white/25 text-xs">{s.dateOfPayment}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-3 flex items-center justify-between flex-wrap gap-2">
                <button
                  onClick={() => exportResearchStudiesCsv(studies, name, npi, EXPORT_LIMIT)}
                  className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-white/10 text-white/40 hover:text-white/70 hover:border-white/20 transition-all"
                >
                  ⬇ Export studies to CSV
                </button>
                {studies.length > 10 && (
                  <p className="text-white/25 text-xs">
                    Showing 10 of {studies.length} {studies.length === 1 ? "study" : "studies"} · export for full list.
                  </p>
                )}
              </div>
            </>
        </div>
      )}

      <p className="print:hidden text-white/20 text-xs text-center mt-8">
        Source: CMS Open Payments Research · Payments disclosed by manufacturers as required by the Physician Payments Sunshine Act
      </p>
      <p className="hidden print:block text-gray-400 text-xs text-center mt-8 border-t border-gray-200 pt-4">
        Source: CMS Open Payments Research (2016–2025) · Reported by applicable manufacturers and GPOs as required by the Physician Payments Sunshine Act · This report supports compliance research and risk review — consult qualified legal or compliance counsel for final determinations · providerpaywatch.com · Generated {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
      </p>
    </main>
  );
}

// ── Export functions ──────────────────────────────────────────────────────────
function exportResearchDetailCsv(detail, name, npi, directResearch, exportLimit = 500) {
  const rows = [["Provider Name", "NPI", "Company", "Year", "Payment Type", "Total Amount ($)",
    "Payment Count", "Study Count", "Context", "Expenditure Category", "Product", "NCT ID"]];
  const exportDetailData = exportLimit === Infinity ? detail : detail.slice(0, exportLimit);
  const exportDirectData = exportLimit === Infinity ? (directResearch || []) : (directResearch || []).slice(0, Math.max(0, exportLimit - exportDetailData.length));

  // PI-based rows
  for (const d of exportDetailData) {
    rows.push([
      name, npi, d.companyName, d.programYear, "pi_based",
      d.totalAmount.toFixed(2), d.paymentCount, d.studyCount,
      d.contextOfResearch, d.expenditureCategory1, d.productName, d.nctId,
    ]);
  }

  // Direct research rows — blank for fields not available in physician_direct_research_agg
  for (const d of exportDirectData) {
    rows.push([
      name, npi, d.companyName, d.year, "direct",
      d.totalAmount.toFixed(2), d.paymentCount, "",
      "", "", d.productName || "", "",
    ]);
  }

  // Sort by year then payment_type for readability
  const header = rows.shift();
  rows.sort((a, b) => {
    if (a[3] !== b[3]) return a[3] - b[3]; // year ascending
    return a[4].localeCompare(b[4]);        // payment_type alphabetical (direct before pi_based)
  });
  rows.unshift(header);

  downloadCsv(rows, `${(name || "provider").replace(/\s+/g, "_")}_research_payment_history.csv`);
}

function exportResearchCompaniesCsv(research, name, npi, tripleInterestCompanyIds, dualInterest, researchGeneralDual, paymentOwnershipDual, exportLimit = 500, directResearch = []) {
  // Build direct totals map by companyId for quick lookup
  const directByCompany = directResearch.reduce((acc, d) => {
    if (!acc[d.companyId]) acc[d.companyId] = { totalAmount: 0, paymentCount: 0 };
    acc[d.companyId].totalAmount += d.totalAmount;
    acc[d.companyId].paymentCount += d.paymentCount;
    return acc;
  }, {});

  const rows = [["Provider Name", "NPI", "Company", "Associated Research ($)", "PI Payment Count",
    "Study Count", "Direct Payments ($)", "Direct Payment Count", "First Year", "Latest Year",
    "Active 2025", "Context", "Primary Product", "NCT ID", "Interest Flag"]];
  const exportData = exportLimit === Infinity ? research : research.slice(0, exportLimit);
  for (const r of exportData) {
    const isTriple = tripleInterestCompanyIds.has(r.companyId);
    const isDual = !isTriple && (
      dualInterest.some((d) => d.companyId === r.companyId) ||
      researchGeneralDual.some((g) => g.companyId === r.companyId) ||
      paymentOwnershipDual.some((p) => p.companyId === r.companyId)
    );
    const flag = isTriple ? "Triple Interest" : isDual ? "Dual Interest" : "";
    const direct = directByCompany[r.companyId] || { totalAmount: 0, paymentCount: 0 };
    rows.push([
      name, npi, r.companyName,
      r.isDirectOnly ? "" : r.totalAmount.toFixed(2),
      r.isDirectOnly ? "" : r.paymentCount,
      r.isDirectOnly ? "" : (r.studyCount || 0),
      direct.totalAmount > 0 ? direct.totalAmount.toFixed(2) : "",
      direct.paymentCount > 0 ? direct.paymentCount : "",
      r.firstYear, r.latestYear,
      r.hasActiveResearch ? "Yes" : "No",
      r.primaryContext || "", r.primaryProduct || "", r.latestNctId || "", flag,
    ]);
  }
  downloadCsv(rows, `${name.replace(/\s+/g, "_")}_research_companies.csv`);
}

function exportResearchStudiesCsv(studies, name, npi, exportLimit = 500) {
  const rows = [["Provider Name", "NPI", "Company", "Study Name", "Year", "Amount ($)",
    "Date", "Context", "Preclinical", "Expenditure Category", "Product Type", "Product Name", "NCT ID"]];
  const exportData = exportLimit === Infinity ? studies : studies.slice(0, exportLimit);
  for (const s of exportData) {
    rows.push([
      name, npi, s.companyName, s.nameOfStudy, s.programYear,
      s.totalAmount.toFixed(2), s.dateOfPayment, s.contextOfResearch,
      s.preclinicalIndicator === "Y" ? "Yes" : "No",
      s.expenditureCategory1, s.productType, s.productName, s.nctId,
    ]);
  }
  downloadCsv(rows, `${name.replace(/\s+/g, "_")}_research_studies.csv`);
}

function exportPreclinicalCsv(companies, name, npi) {
  const rows = [["Provider Name", "NPI", "Company", "Type", "Clinical Payments",
    "Clinical Amount ($)", "Preclinical Payments", "Preclinical Amount ($)",
    "Not Reported Payments", "Not Reported Amount ($)", "Total Amount ($)"]];
  for (const c of companies) {
    const total = c.clinicalAmount + c.preclinicalAmount + c.unknownAmount;
    const type = c.preclinical > 0 && c.clinical > 0 ? "Mixed"
      : c.preclinical > 0 ? "Preclinical"
      : "Clinical";
    rows.push([
      name, npi, c.companyName, type,
      c.clinical, c.clinicalAmount.toFixed(2),
      c.preclinical, c.preclinicalAmount.toFixed(2),
      c.unknown, c.unknownAmount.toFixed(2),
      total.toFixed(2),
    ]);
  }
  downloadCsv(rows, `${(name || "provider").replace(/\s+/g, "_")}_preclinical_split.csv`);
}

function exportProductExposureCsv(products, name, npi) {
  const rows = [["Provider Name", "NPI", "Product Name", "Product Type",
    "Total Funding ($)", "Payment Count", "Company Count", "First Year", "Latest Year"]];
  for (const p of products) {
    rows.push([
      name, npi, p.name, p.type,
      p.totalAmount.toFixed(2), p.paymentCount,
      p.companyCount || 1,
      p.firstYear || "", p.latestYear || "",
    ]);
  }
  downloadCsv(rows, `${(name || "provider").replace(/\s+/g, "_")}_product_exposure.csv`);
}

function downloadCsv(rows, filename) {
  const csv = rows.map((r) => r.map((v) => `"${v ?? ""}"`).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
}